import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ConsultationIpadView.css";
import api from "./api";
import useAuth from "../hooks/useAuth";
import VideoAssignModal from "./VideoAssignModal";
import PatientProgressModal from "./PatientProgressModal";
import TreatmentPlanModal from "./TreatmentPlanModal";
import ConsultationHistoryModal from "./ConsultationHistoryModal";
import SessionReviewModal from "./SessionReviewModal";
import PatientReportsModal from "./PatientReportsModal";
import { useDialog } from './useDialog';
import { isSuper } from '../utils/canDo';

/* Body part → stype mapping is loaded only from backend /bodyparts. */

/* Body-part key → consultation schema field (for save payload) */
const PART_TO_SCHEMA_SINGLE = {
  cervical: "cervical", upperback: "upperback", middleback: "middleback",
  lowerback: "lowerback", heart: "heart", digestive: "digestive",
  abdomen: "abdomen", renal: "renal", reproductive: "reproductive",
};
const PART_TO_SCHEMA_ARRAY = {
  shoulder_L: "shoulder", shoulder_R: "shoulder",
  shoulder_LB: "shoulder", shoulder_RB: "shoulder",
  arm_L: "arm", arm_R: "arm", arm_LB: "arm", arm_RB: "arm",
  elbow_L: "elbow", elbow_R: "elbow", elbow_LB: "elbow", elbow_RB: "elbow",
  wrist_L: "wrist", wrist_R: "wrist",
  palm_L: "hand", palm_R: "hand",
  fingers_L: "hand", fingers_R: "hand",
  hip_L: "hip", hip_R: "hip", hip_LB: "hip", hip_RB: "hip",
  thigh_L: "thigh", thigh_R: "thigh",
  hamstring_L: "leg", hamstring_R: "leg",
  leg_L: "leg", leg_R: "leg",
  knee_L: "knee", knee_R: "knee", knee_LB: "knee", knee_RB: "knee",
  calf_L: "calf", calf_R: "calf", calf_LB: "calf", calf_RB: "calf",
  ankle_L: "ankle", ankle_R: "ankle", ankle_LB: "ankle", ankle_RB: "ankle",
  foot_L: "foot", foot_R: "foot", heel_L: "foot", heel_R: "foot",
  toes_L: "toes", toes_R: "toes",
  lung: "lung", chest: "chest", coccyx: "coccyx",
};
const PART_SIDE = {
  shoulder_L: "left", shoulder_R: "right", shoulder_LB: "left", shoulder_RB: "right",
  arm_L: "left", arm_R: "right", arm_LB: "left", arm_RB: "right",
  elbow_L: "left", elbow_R: "right", elbow_LB: "left", elbow_RB: "right",
  wrist_L: "left", wrist_R: "right",
  palm_L: "left", palm_R: "right",
  fingers_L: "left", fingers_R: "right",
  hip_L: "left", hip_R: "right", hip_LB: "left", hip_RB: "right",
  thigh_L: "left", thigh_R: "right",
  hamstring_L: "left", hamstring_R: "right",
  leg_L: "left", leg_R: "right",
  knee_L: "left", knee_R: "right", knee_LB: "left", knee_RB: "right",
  calf_L: "left", calf_R: "right", calf_LB: "left", calf_RB: "right",
  ankle_L: "left", ankle_R: "right", ankle_LB: "left", ankle_RB: "right",
  foot_L: "left", foot_R: "right",
  heel_L: "left", heel_R: "right",
  toes_L: "left", toes_R: "right",
};

const SYMPTOM_LIST = [
  { key: "pain",        label: "Pain",        color: "#4a9d5e", icon: "✓"  },
  { key: "numbness",    label: "Numbness",    color: "#d4a017", icon: "◉"  },
  { key: "weakness",    label: "Weakness",    color: "#d4a017", icon: "◉"  },
  { key: "stiffness",   label: "Stiffness",   color: "#3b6ea8", icon: "⊟"  },
  { key: "burning",     label: "Burning",     color: "#3b6ea8", icon: "+"  },
  { key: "spasm",       label: "Spasm",       color: "#3b6ea8", icon: "✓"  },
  { key: "instability", label: "Instability", color: "#3b6ea8", icon: "◆"  },
];
const SCORE_OPTS = [1, 3, 4, 6, 7, 8, 10];

/* ─── Duration helpers (stored as total seconds) ──────── */
// If durationUnit is explicitly set, use it; else legacy guess (< 60 = minutes)
function normalizeDur(raw, unit) {
  const d = Number(raw) || 0;
  if (d <= 0) return 60;
  if (unit === 'seconds') return d;
  if (unit === 'minutes') return d * 60;
  if (d < 60) return d * 60; // legacy: assume minutes
  return d;
}
function secsToMS(totalSecs) {
  const t = Number(totalSecs) || 0;
  return { m: Math.floor(t / 60), s: t % 60 };
}
function msToSecs(m, s) {
  return (Number(m) || 0) * 60 + (Number(s) || 0);
}

function getPainColor(score) {
  if (!score || score === 0) return "rgba(160,175,200,0.22)";
  if (score >= 7) return "rgba(220,40,30,0.96)";   // vivid red – severe
  if (score >= 4) return "rgba(235,120,10,0.96)";  // vivid orange – moderate
  return "rgba(50,190,70,0.92)";                    // vivid green – mild
}
function getPainStroke(score) {
  if (!score || score === 0) return "rgba(130,140,160,0.40)";
  if (score >= 7) return "rgba(180,20,10,1)";
  if (score >= 4) return "rgba(200,90,0,1)";
  return "rgba(20,160,45,1)";
}
function getPainGlow(score) {
  if (!score || score === 0) return "none";
  if (score >= 7) return "drop-shadow(0 0 8px rgba(220,40,30,0.80)) drop-shadow(0 0 3px rgba(255,100,80,0.60))";
  if (score >= 4) return "drop-shadow(0 0 7px rgba(235,120,10,0.75))";
  return "drop-shadow(0 0 6px rgba(50,190,70,0.65))";
}

const LS_KEY = (card) => `civPayload-${card}`;

// Perpendicular unit-vector (same as BPSelection) — used for triple-line offset in PainLine
function perpVec(x1, y1, x2, y2, d) {
  const dx = x2 - x1, dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy) || 1;
  return { px: (-dy / len) * d, py: (dx / len) * d };
}

/* ═══════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════ */
export default function ConsultationIpadView({ patientCard: propCard }) {
  const { patientCard: pcParam } = useParams();
  const navigate = useNavigate();

  const { dialogNode, showAlert, showConfirm } = useDialog();

  /* search */
  const [searchInput, setSearchInput]     = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching]     = useState(false);
  const debounceRef  = useRef(null);
  const isDirtyRef   = useRef(false); // true only after user makes a change

  /* data */
  const [patientCard, setPatientCard]   = useState(propCard || pcParam || "");
  const [profile, setProfile]           = useState(null);
  const [preConsult, setPreConsult]     = useState(null);
  const [appointment, setAppointment]   = useState(null);
  const [allSessions, setAllSessions]   = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState("");

  /* toast */
  const [civToast, setCivToast] = useState(null); // { message, type }
  const civToastTimer = useRef(null);
  const showToast = (message, type = 'error') => {
    clearTimeout(civToastTimer.current);
    setCivToast({ message, type });
    civToastTimer.current = setTimeout(() => setCivToast(null), 4000);
  };

  /* body symptoms: { partKey: { pain, numbness, weakness, stiffness, burning, spasm, instability } } */
  const [bodySymptoms, setBodySymptoms] = useState({});
  const [selectedPart, setSelectedPart] = useState(null);       // currently focused body part (symptom panel open)
  const [removeConfirm, setRemoveConfirm] = useState(null);     // { partKey, label, exercises[] } for removal confirmation modal
  const [prevConsultParts, setPrevConsultParts] = useState(new Set()); // parts from previous DB consultation
  const [prevDbSymptoms, setPrevDbSymptoms]     = useState({});          // { partKey: { pain, ... } } from last saved consultation
  const [romProgressData, setRomProgressData]   = useState({});          // { partKey: [{label, value}] }
  const [mvcProgressData, setMvcProgressData]   = useState({});          // { partKey: [{label, value}] }
  const [consultation, setConsultation]         = useState(null);        // raw consultation from BE
  const [showVideoModal, setShowVideoModal]     = useState(false);
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [showPlanModal, setShowPlanModal]         = useState(false);
  const [showHistoryModal, setShowHistoryModal]   = useState(false);
  const [showReviewModal, setShowReviewModal]     = useState(false);
  const [showReportsModal, setShowReportsModal]   = useState(false);
  const [resetting, setResetting]                 = useState(false);
  const [partComments, setPartComments]           = useState({});   // { partKey: string }
  const [generalComments, setGeneralComments]     = useState("");
  const { auth } = useAuth();
  const isOwnerOrSolution = isSuper(auth);
  const [symptomGraphData, setSymptomGraphData] = useState({}); // { symptomKey: [{label, value}] }
  const [showMyNotes, setShowMyNotes]       = useState(false);
  const [myNotes, setMyNotes]               = useState([]);
  const [myNotesLoading, setMyNotesLoading] = useState(false);
  const [newNoteText, setNewNoteText]       = useState('');
  const [editingNoteId, setEditingNoteId]   = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  // OLD GTP Update
  const [showOldGtpModal, setShowOldGtpModal] = useState(false);
  const [oldGtpServices, setOldGtpServices]   = useState([]); // [{ name, qty, rate, sgst, cgst, igst, ecess }]
  const [oldGtpAllSvcs, setOldGtpAllSvcs]     = useState([]);
  const [oldGtpPaid, setOldGtpPaid]           = useState(0);
  const [oldGtpSaving, setOldGtpSaving]       = useState(false);
  const [oldGtpSearch, setOldGtpSearch]       = useState('');
  const [oldGtpConfirm, setOldGtpConfirm]     = useState(false); // show inline confirm step
  const [oldGtpToast, setOldGtpToast]         = useState(null);  // { type: 'success'|'error', msg }

  /* sequences panel */
  const [availableSeqs, setAvailableSeqs]   = useState([]);  // from BE for current stype
  const [selectedSeqs, setSelectedSeqs]     = useState([]);  // currently selected seq objects
  const [planExcludedParts, setPlanExcludedParts] = useState([]);
  const [planPartHolds,     setPlanPartHolds]     = useState({});
  const [planRomLinkedSelections, setPlanRomLinkedSelections] = useState({});
  const [seqsLoading, setSeqsLoading]       = useState(false);
  const [seqSaveMsg, setSeqSaveMsg]         = useState("");
  const [seqSearch, setSeqSearch]           = useState("");

  /* recommendations */
  const [allRecomms, setAllRecomms]         = useState([]);  // all system master recommendations
  const [selectedRecomms, setSelectedRecomms] = useState([]); // patient's chosen recommendations

  /* body-map config — loaded only from backend. No hardcoded bodypart/stype fallback. */
  const [bodyPartsConfig, setBodyPartsConfig] = useState({ front: [], back: [] });
  const [bodyPartsLoaded, setBodyPartsLoaded] = useState(false);

  /* PART_TO_STYPE — built from backend bodyparts config only.
     Supports common backend field names, then falls back to the BE bodypart key
     so the FE still calls the sequence API instead of silently stopping. */
  const allBodyParts = useMemo(() => [
    ...(bodyPartsConfig.front || []),
    ...(bodyPartsConfig.back || []),
  ], [bodyPartsConfig]);

  const getSequenceTypeFromBodyPart = (p) => {
    if (!p) return '';
    const raw =
      p.stype ??
      p.sequenceType ??
      p.seqType ??
      p.sequence_type ??
      p.validStype ??
      p.validSType ??
      p.sType ??
      p.type ??
      p.rtype ??
      p.category ??
      p.group ??
      p.bodyPartType ??
      p.key; // last-resort BE value; not a hardcoded fallback

    if (Array.isArray(raw)) return String(raw[0] || '').trim();
    return String(raw || '').trim();
  };

  const PART_TO_STYPE = useMemo(() => {
    const map = {};
    allBodyParts.forEach(p => {
      if (!p?.key) return;
      map[p.key] = getSequenceTypeFromBodyPart(p);
    });
    return map;
  }, [allBodyParts]);

  const selectedBodyPartConfig = useMemo(
    () => allBodyParts.find(p => p.key === selectedPart) || null,
    [allBodyParts, selectedPart]
  );

  const selectedStype = selectedPart ? (PART_TO_STYPE[selectedPart] || selectedPart) : '';

  /* ── Computed body part readiness: blue = ready, pink = incomplete, none = untouched ── */
  const bodyPartStatus = useMemo(() => {
    // status per part: 'ready' (blue) | 'incomplete' (pink) | null (untouched)
    const status = {};
    // Build map: partKey → has exercises (via sourcePart on selected sequences)
    const partsWithExercises = new Set();
    selectedSeqs.forEach(seq => {
      if (seq.sourcePart) partsWithExercises.add(seq.sourcePart);
    });
    // Collect all relevant part keys (from symptoms OR exercises)
    const allPartKeys = new Set([...Object.keys(bodySymptoms), ...partsWithExercises]);
    allPartKeys.forEach(partKey => {
      const s = bodySymptoms[partKey] || {};
      const hasAnySymptom = Object.values(s).some(v => v > 0);
      const hasExercises = partsWithExercises.has(partKey);
      if (hasAnySymptom && hasExercises) {
        status[partKey] = 'ready';       // blue — has both symptoms + exercises
      } else if (hasAnySymptom || hasExercises) {
        status[partKey] = 'incomplete';  // pink — one of the two is missing
      }
    });
    return status;
  }, [bodySymptoms, selectedSeqs]);

  /* body-map zoom + rotation + front/back toggle */
  const [bodyView,       setBodyView]       = useState('front');   // 'front' | 'back'
  const [isFlipping,     setIsFlipping]     = useState(null);      // null | 'out' | 'in'
  const [flipDir,        setFlipDir]        = useState(1);         // 1 = front→back, -1 = back→front
  const DEFAULT_ZOOM = 1.0;
  const [frontZoom, setFrontZoom] = useState(DEFAULT_ZOOM);
  const [frontRot,  setFrontRot]  = useState(0);
  const [backZoom,  setBackZoom]  = useState(DEFAULT_ZOOM);
  const [backRot,   setBackRot]   = useState(0);
  const [frontPanX, setFrontPanX] = useState(0);
  const [frontPanY, setFrontPanY] = useState(0);
  const [backPanX,  setBackPanX]  = useState(0);
  const [backPanY,  setBackPanY]  = useState(0);
  const dragRef = useRef(null); // { startX, startY, origPanX, origPanY, didDrag }

  const bvBtn = { background:'rgba(45,95,160,0.09)', border:'1px solid rgba(45,95,160,0.22)', borderRadius:5, cursor:'pointer', color:'#1e3a5f', fontWeight:700, fontSize:11, padding:'2px 7px', lineHeight:'1.5', whiteSpace:'nowrap' };
  const bvVal = { fontSize:11, fontWeight:700, color:'#4b5563', minWidth:34, textAlign:'center', display:'inline-block' };

  /* ── auto-load ───────────────────────────────────────── */
  useEffect(() => {
    if (!bodyPartsLoaded) return;
    if (propCard) { setPatientCard(propCard); loadPatientData(propCard); }
    // eslint-disable-next-line
  }, [propCard, bodyPartsLoaded]);

  useEffect(() => {
    if (!bodyPartsLoaded) return;
    if (pcParam && !propCard) { setPatientCard(pcParam); loadPatientData(pcParam); }
    // eslint-disable-next-line
  }, [pcParam, propCard, bodyPartsLoaded]);

  /* ── fetch body-part config from backend (admin-configured via BPSelection) ── */
  useEffect(() => {
    setBodyPartsLoaded(false);
    api.get(`${process.env.REACT_APP_URL}/bodyparts`)
      .then(res => {
        setBodyPartsConfig({
          front: Array.isArray(res.data?.front) ? res.data.front : [],
          back:  Array.isArray(res.data?.back)  ? res.data.back  : [],
        });
      })
      .catch(err => {
        console.error('Failed to load bodyparts from BE', err);
        setBodyPartsConfig({ front: [], back: [] });
      })
      .finally(() => setBodyPartsLoaded(true));
  }, []);

  /* ── search debounce ─────────────────────────────────── */
  useEffect(() => {
    if (searchInput.length < 3) { setSearchResults([]); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const r = await api.get(`${process.env.REACT_APP_URL}/patients/profiles/search/${searchInput}`);
        setSearchResults(r.data || []);
      } catch (e) { setSearchResults([]); }
      finally { setIsSearching(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  /* ── fetch sequences when body part changes ────────────
     BE-only mode: selectedPart must have stype in /bodyparts response.
     Every clicked part appends its fetched sequences to the available list. */
  useEffect(() => {
    if (!bodyPartsLoaded) return;

    if (!selectedPart || !patientCard) {
      setSeqsLoading(false);
      return;
    }

    if (!selectedBodyPartConfig) {
      console.warn('Selected bodypart key was not found in backend /bodyparts config:', selectedPart);
      setSeqsLoading(false);
      setAvailableSeqs([]);
      return;
    }

    let cancelled = false;
    setSeqsLoading(true);
    setSeqSaveMsg("");
    setSeqSearch("");

    console.log('SEQ FETCH', {
      selectedPart,
      selectedStype,
      selectedBodyPartConfig,
      url: `${process.env.REACT_APP_URL}/sequences/${patientCard}/type/${encodeURIComponent(selectedStype)}`,
    });

    api.get(`${process.env.REACT_APP_URL}/sequences/${patientCard}/type/${encodeURIComponent(selectedStype)}`)
      .then(r => {
        if (cancelled) return;

        const seqs = (r.data || []).map(s => ({
          ...s,
          sourcePart:  selectedPart,
          sourceStype: selectedStype,
          duration:    s.duration   || 60,
          repetition:  s.repetition || 1,
          remarks:     s.remarks    || "",
          _selected:   false,
        }));

        // Show only sequences for the currently selected body part (not accumulated)
        setAvailableSeqs(seqs);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Sequence fetch failed:', {
          selectedPart,
          selectedStype,
          patientCard,
          err,
        });
      })
      .finally(() => {
        if (!cancelled) setSeqsLoading(false);
      });

    return () => { cancelled = true; };
  }, [bodyPartsLoaded, selectedPart, patientCard, selectedStype, selectedBodyPartConfig]);


  /* ── persist to localStorage whenever symptoms, recomms, or comments change ── */
  useEffect(() => {
    if (!patientCard) return;
    try {
      localStorage.setItem(LS_KEY(patientCard), JSON.stringify({
        // NEW: store annotation-keyed symptoms directly for perfect round-trip
        _bodySymptoms:   bodySymptoms,
        _recomms:        selectedRecomms,
        _partComments:   partComments,
        _generalComments: generalComments,
        _partKeys:       Object.keys(bodySymptoms),
        _dirty:          isDirtyRef.current,
      }));
    } catch (_) {}
  }, [bodySymptoms, selectedRecomms, partComments, generalComments, patientCard]);

  /* ── load patient data ───────────────────────────────── */
  const loadPatientData = async (card) => {
    setLoading(true);
    setError("");

    // Prevent sequence/bodypart state from leaking from the previous patient.
    setSelectedSeqs([]);
    setAvailableSeqs([]);
    setSelectedPart(null);
    setSeqSearch("");
    setSeqSaveMsg("");
    setSeqsLoading(false);

    isDirtyRef.current = false; // reset: data load is not a "user change"
    try {
      const [profileRes, consultRes, preRes, apptRes, sessRes, allRecRes] = await Promise.all([
        api.get(`${process.env.REACT_APP_URL}/patients/profiles/${card}`),
        api.get(`${process.env.REACT_APP_URL}/patients/consultation/${card}`),
        api.get(`${process.env.REACT_APP_URL}/patients/preconsultation/${card}`).catch(() => ({ data: null })),
        api.get(`${process.env.REACT_APP_URL}/appointments/patient/${card}/today`).catch(() => ({ data: [] })),
        api.get(`${process.env.REACT_APP_URL}/appointments/type/Therapy/patient/${card}`).catch(() => ({ data: [] })),
        api.get(`${process.env.REACT_APP_URL}/recommendations`).catch(() => ({ data: [] })),
      ]);

      setProfile(profileRes.data);
      setPreConsult(preRes.data);
      setConsultation(consultRes.data);
      setPlanExcludedParts(consultRes.data?.planExcludedParts || []);
      setPlanPartHolds(consultRes.data?.planPartHolds || {});
      setPlanRomLinkedSelections(consultRes.data?.planRomLinkedSelections || {});
      // Load comments from DB as baseline (localStorage may override below)
      setGeneralComments(consultRes.data?.generalComments || '');
      setPartComments(consultRes.data?.partComments || {});
      setAllSessions(sessRes.data || []);
      const appts = apptRes.data || [];
      setAppointment(Array.isArray(appts) ? (appts[0] || null) : appts);

      // Extract ALL body-part symptoms from DB (full history preserved)
      const allDbSymptoms = extractSymptomsFromConsultation(consultRes.data || {});
console.log("allDbSym = and consultRes.data = ", allDbSymptoms, consultRes.data || {});
      // Excluded parts: resolved for now — suppress from SVG highlighting and
      // from the active editing view. DB data stays intact for history access.
      // Doctor can re-activate a part by clicking it in the SVG and entering new symptoms.
      const excludedSet = new Set(consultRes.data?.planExcludedParts || []);
      const dbSymptoms  = Object.fromEntries(
        Object.entries(allDbSymptoms).filter(([key]) => !excludedSet.has(key))
      );

      // prevConsultParts / prevDbSymptoms only reflect active (non-excluded) parts
      // so the SVG "prev data" glow doesn't appear for excluded parts either
      const consultParts = new Set(Object.keys(dbSymptoms));
      setPrevConsultParts(consultParts);
      setPrevDbSymptoms(dbSymptoms);

      // Check localStorage first for unsaved edits, fall back to DB
      let initialSymptoms = dbSymptoms;
      let initialRecomms = consultRes.data?.recommendations || [];
      try {
        const saved = localStorage.getItem(LS_KEY(card));
        if (saved) {
          const parsedLS = JSON.parse(saved);
          // NEW path: direct annotation-keyed symptoms (exact round-trip)
          if (parsedLS._bodySymptoms && typeof parsedLS._bodySymptoms === 'object' && Object.keys(parsedLS._bodySymptoms).length > 0 && parsedLS._dirty === true) {
            initialSymptoms = parsedLS._bodySymptoms;
          } else {
            // Legacy path: schema-field-encoded symptoms
            const localSymptoms = parseLocalStoragePayload(parsedLS);
            if ((Array.isArray(parsedLS._partKeys) && parsedLS._dirty === true) || Object.keys(localSymptoms).length > 0) {
              initialSymptoms = localSymptoms;
            }
          }
          if (Array.isArray(parsedLS._recomms) && parsedLS._recomms.length > 0) {
            initialRecomms = parsedLS._recomms;
          }
          if (parsedLS._partComments && typeof parsedLS._partComments === 'object') {
            setPartComments(parsedLS._partComments);
          }
          if (typeof parsedLS._generalComments === 'string') {
            setGeneralComments(parsedLS._generalComments);
          }
        }
      } catch (_) {}

      // Filter out body parts where ALL symptom scores are zero (not being consulted)
      const filteredSymptoms = Object.fromEntries(
        Object.entries(initialSymptoms).filter(([, s]) => Object.values(s).some(v => v > 0))
      );
      setBodySymptoms(filteredSymptoms);
      setSelectedPart(null); // always reset so symptom panel doesn't show stale data
      setAllRecomms(allRecRes.data || []);
      setSelectedRecomms(initialRecomms);

      // Pre-populate sequences — restore all previously-saved sequences.
      // These were already selected for this patient's treatment plan, so show them all.
      // Deduplicate by _id or name.
      const savedSeqs = Array.isArray(consultRes.data?.sequences) ? consultRes.data.sequences : [];

      if (savedSeqs.length > 0) {
        const seen = new Set();
        const deduped = savedSeqs.filter(s => {
          const k = s._id || s.name;
          if (!k || seen.has(k)) return false;
          seen.add(k);
          return true;
        });

        setSelectedSeqs(deduped.map(s => ({
          ...s,
          duration:   s.duration   || 60,
          repetition: s.repetition || 1,
          remarks:    s.remarks    || "",
          _selected:  true,
        })));
      } else {
        setSelectedSeqs([]);
      }

      // Build progress checkpoints from sessions
      const sessions = sessRes.data || [];
      setSymptomGraphData(buildSymptomGraphData(sessions));

      // Build per-body-part ROM/MVC progress from completed sessions
      const completedSess = sessions.filter(s => s.status === "Completed" && s.sessionFeedback);
      const romProg = {}, mvcProg = {};
      completedSess.forEach((s, si) => {
        Object.entries(s.sessionFeedback?.romByPart || {}).forEach(([part, val]) => {
          if (!romProg[part]) romProg[part] = [];
          romProg[part].push({ label: `S${si + 1}`, value: Number(val) || 0 });
        });
        Object.entries(s.sessionFeedback?.mvcByPart || {}).forEach(([part, val]) => {
          if (!mvcProg[part]) mvcProg[part] = [];
          mvcProg[part].push({ label: `S${si + 1}`, value: Number(val) || 0 });
        });
      });
      setRomProgressData(romProg);
      setMvcProgressData(mvcProg);

      // Auto-select first part from previous consultation (any data), fallback to first with pain
      const firstConsultPart = Array.from(consultParts)[0];
      const firstPainPart = Object.entries(initialSymptoms).find(([, s]) => (s.pain || 0) > 0)?.[0];
      const autoSelect = firstConsultPart || firstPainPart;
      if (autoSelect) setSelectedPart(autoSelect);

    } catch (err) {
      setError("Failed to load: " + (err.response?.data || err.message || "Unknown error"));
    } finally { setLoading(false); }
  };

  /* ── extract body symptoms from consultation schema ─────── */
  function extractSymptomsFromConsultation(cons) {
    // NEW: check bodyParts[] first — exact annotation-key round-trip (no key distortion)
    if (Array.isArray(cons.bodyParts) && cons.bodyParts.length > 0) {
      const syms = {};
      cons.bodyParts.forEach(bp => {
        if (bp.partKey && bp.symptoms && Object.values(bp.symptoms).some(v => Number(v) > 0)) {
          syms[bp.partKey] = {
            pain:        Number(bp.symptoms.pain        || 0),
            numbness:    Number(bp.symptoms.numbness    || 0),
            weakness:    Number(bp.symptoms.weakness    || 0),
            stiffness:   Number(bp.symptoms.stiffness   || 0),
            burning:     Number(bp.symptoms.burning     || 0),
            spasm:       Number(bp.symptoms.spasm       || 0),
            instability: Number(bp.symptoms.instability || 0),
          };
        }
      });
      if (Object.keys(syms).length > 0) return syms;
    }
    // Legacy: fixed-field extraction for old consultation records
    const syms = {};
    const single = ["lowerback","middleback","upperback","cervical","heart","digestive","abdomen","renal","reproductive"];
    single.forEach(part => {
      if (cons[part]?.grade) {
        const g = parseInt(cons[part].grade) || 0;
        const ans = cons[part].answers || {};
        syms[part] = {
          pain:        g,
          numbness:    cons[part].numbness ? Math.max(1, Math.round(g * 0.7)) : 0,
          weakness:    ans.weakness    || 0,
          stiffness:   cons[part].stiffnes ? Math.max(1, Math.round(g * 0.5)) : 0,
          burning:     ans.burning     || 0,
          spasm:       ans.spasm       || 0,
          instability: ans.instability || 0,
        };
      }
    });
    const arrayParts = ["knee","shoulder","leg","ankle","foot","thigh","arm","wrist","calf","chest","coccyx","lung","other"];
    arrayParts.forEach(part => {
      if (!Array.isArray(cons[part])) return;
      cons[part].forEach((entry, idx) => {
        if (!entry?.grade) return;
        const g = parseInt(entry.grade) || 0;
        const side = entry.painside === "left" ? "_L" : (entry.painside === "right" ? "_R" : (idx === 0 ? "_L" : "_R"));
        const key = part + side;
        const ans = entry.answers || {};
        syms[key] = {
          pain:        g,
          numbness:    entry.numbness ? Math.max(1, Math.round(g * 0.7)) : 0,
          weakness:    ans.weakness    || 0,
          stiffness:   entry.stiffnes ? Math.max(1, Math.round(g * 0.5)) : 0,
          burning:     ans.burning     || 0,
          spasm:       ans.spasm       || 0,
          instability: ans.instability || 0,
        };
      });
    });
    // Merge array parts that have no left/right split in the body map
    ['lung', 'chest', 'coccyx'].forEach(p => {
      if (syms[p + '_L'] && !syms[p]) syms[p] = syms[p + '_L'];
      else if (syms[p + '_R'] && !syms[p]) syms[p] = syms[p + '_R'];
      delete syms[p + '_L']; delete syms[p + '_R'];
    });
    return syms;
  }

  /* ── build consultation payload for BE save ─────────────── */
  function buildConsultationPayload(syms) {
    const payload = {};
    Object.entries(syms).forEach(([partKey, s]) => {
      const schemaField = PART_TO_SCHEMA_SINGLE[partKey] || PART_TO_SCHEMA_ARRAY[partKey];
      if (!schemaField) return;
      const entry = {
        grade:    String(s.pain || 0),
        numbness: (s.numbness || 0) > 0,
        stiffnes: (s.stiffness || 0) > 0,
        painside: PART_SIDE[partKey] || "both",
        answers:  {
          weakness:    s.weakness    || 0,
          burning:     s.burning     || 0,
          spasm:       s.spasm       || 0,
          instability: s.instability || 0,
        },
      };
      if (PART_TO_SCHEMA_SINGLE[partKey]) {
        payload[schemaField] = entry;
      } else {
        if (!payload[schemaField]) payload[schemaField] = [];
        // Update or push based on side
        const side = PART_SIDE[partKey];
        const existing = payload[schemaField].findIndex(e => e.painside === side);
        if (existing >= 0) payload[schemaField][existing] = entry;
        else payload[schemaField].push(entry);
      }
    });
    return payload;
  }

  /* ── parse localStorage payload back to bodySymptoms format ── */
  function parseLocalStoragePayload(payload) {
    const syms = {};
    const single = ["lowerback","middleback","upperback","cervical","heart","digestive","abdomen","renal","reproductive"];
    single.forEach(field => {
      if (payload[field]) {
        const g = parseInt(payload[field].grade) || 0;
        const ans = payload[field].answers || {};
        syms[field] = {
          pain:        g,
          numbness:    payload[field].numbness ? Math.max(1, Math.round(g*0.7)) : 0,
          weakness:    ans.weakness    || 0,
          stiffness:   payload[field].stiffnes ? Math.max(1, Math.round(g*0.5)) : 0,
          burning:     ans.burning     || 0,
          spasm:       ans.spasm       || 0,
          instability: ans.instability || 0,
        };
      }
    });
    const arrayFields = ["knee","shoulder","leg","ankle","foot","thigh","arm","wrist","calf","chest","coccyx","lung"];
    arrayFields.forEach(field => {
      if (!Array.isArray(payload[field])) return;
      payload[field].forEach(entry => {
        const side = entry.painside === "left" ? "_L" : "_R";
        const key = field + side;
        const g = parseInt(entry.grade) || 0;
        const ans = entry.answers || {};
        syms[key] = {
          pain:        g,
          numbness:    entry.numbness ? Math.max(1, Math.round(g*0.7)) : 0,
          weakness:    ans.weakness    || 0,
          stiffness:   entry.stiffnes ? Math.max(1, Math.round(g*0.5)) : 0,
          burning:     ans.burning     || 0,
          spasm:       ans.spasm       || 0,
          instability: ans.instability || 0,
        };
      });
    });
    ['lung', 'chest', 'coccyx'].forEach(p => {
      if (syms[p + '_L'] && !syms[p]) syms[p] = syms[p + '_L'];
      else if (syms[p + '_R'] && !syms[p]) syms[p] = syms[p + '_R'];
      delete syms[p + '_L']; delete syms[p + '_R'];
    });
    return syms;
  }

  function buildSymptomGraphData(sessions) {
    const completed = sessions.filter(s => s.status === "Completed" && s.sessionFeedback);
    if (completed.length === 0) return {};
    const SYMS = ['pain','numbness','weakness','stiffness','burning','spasm','instability'];
    const result = {};
    SYMS.forEach(key => {
      const pts = completed
        .filter(s => s.sessionFeedback[key] != null)
        .map((s, i) => ({
          label: `S${i + 1}`,
          // clamp to [0,10] — lines never go below the baseline (0)
          value: Math.min(10, s.sessionFeedback[key]),
        }));
      if (pts.length > 0) result[key] = pts;
    });
    return result;
  }

  /* ── symptom editing ──────────────────────────────────── */
  const setSymptomScore = (partKey, symptomKey, score) => {
    isDirtyRef.current = true;
    // Pre-check if this change will auto-remove the part (all symptoms become 0)
    const currentPartData = { ...(bodySymptoms[partKey] || {}), [symptomKey]: score };
    const willBeRemoved = Object.values(currentPartData).every(v => !v || Number(v) === 0);
    if (willBeRemoved) {
      const removedStype = PART_TO_STYPE[partKey];
      if (removedStype) {
        const remainingKeys = Object.keys(bodySymptoms).filter(k => k !== partKey);
        const remainingStypes = new Set(remainingKeys.map(k => PART_TO_STYPE[k]).filter(Boolean));
        if (!remainingStypes.has(removedStype)) {
          setSelectedSeqs(prev => prev.filter(seq => {
            const st = Array.isArray(seq.stype) ? seq.stype : (seq.stype ? [seq.stype] : []);
            if (!st.length) return true;
            return st.some(s => remainingStypes.has(s));
          }));
        }
      }
    }
    setBodySymptoms(prev => {
      const partData = { ...(prev[partKey] || {}), [symptomKey]: score };
      const allZero = Object.values(partData).every(v => !v || Number(v) === 0);
      if (allZero) {
        if (selectedPart === partKey) setSelectedPart(null);
        const updated = { ...prev };
        delete updated[partKey];
        return updated;
      }
      return { ...prev, [partKey]: partData };
    });
  };

  /* ── body part click: focus (toggle) ─────────────────────── */
  const handleBodyPartClick = (partKey) => {
    setSelectedPart(prev => prev === partKey ? null : partKey);
  };

  /* ── body part removal with confirmation ───────────────── */
  const handleRemovePartRequest = (partKey) => {
    const partLabel = [...(bodyPartsConfig.front || []), ...(bodyPartsConfig.back || [])].find(p => p.key === partKey)?.label || partKey;
    // Find exercises added from this body part (via sourcePart)
    const exercisesToRemove = selectedSeqs.filter(seq => seq.sourcePart === partKey);
    setRemoveConfirm({ partKey, label: partLabel, exercises: exercisesToRemove });
  };

  const confirmRemovePart = () => {
    if (!removeConfirm) return;
    const { partKey, exercises } = removeConfirm;
    isDirtyRef.current = true;
    // Remove symptoms
    setBodySymptoms(prev => {
      const updated = { ...prev };
      delete updated[partKey];
      return updated;
    });
    // Remove all exercises sourced from this body part
    setSelectedSeqs(prev => prev.filter(seq => seq.sourcePart !== partKey));
    // Clear focus if this was the focused part
    if (selectedPart === partKey) setSelectedPart(null);
    setRemoveConfirm(null);
  };

  /* ── body-view flip animation ─────────────────────────── */
  const handleBodyViewToggle = (newView) => {
    if (newView === bodyView || isFlipping) return;
    const dir = newView === 'back' ? 1 : -1;
    setFlipDir(dir);
    setIsFlipping('out');
    setTimeout(() => {
      setBodyView(newView);
      setIsFlipping('in');
      setTimeout(() => setIsFlipping(null), 650);
    }, 650);
  };

  /* ── sequences panel handlers ─────────────────────────── */
  const toggleSeqSelection = (seq) => {
    setSelectedSeqs(prev => {
      const exists = prev.find(s =>
        (s._id && seq._id && s._id === seq._id) || (s.name && seq.name && s.name === seq.name)
      );
      if (exists) return prev.filter(s =>
        !((s._id && seq._id && s._id === seq._id) || (s.name && seq.name && s.name === seq.name))
      );
      return [...prev, { ...seq, patientCard, status: "notStarted" }];
    });
  };

  const updateSeqField = (seqId, field, value) => {
    setSelectedSeqs(prev => prev.map(s => {
      if (s._id !== seqId && s.name !== seqId) return s;
      const patch = { [field]: value };
      // When duration is edited, mark unit as seconds (it's already converted by msToSecs)
      if (field === 'duration') patch.durationUnit = 'seconds';
      return { ...s, ...patch };
    }));
  };

  /* ── recommendations handlers ──────────────────────────── */
  const toggleRecomm = (rec) => {
    isDirtyRef.current = true;
    setSelectedRecomms(prev => {
      const matches = r => (r._id && rec._id && r._id === rec._id) || r.recommendation === rec.recommendation;
      const exists = prev.find(matches);
      if (exists) return prev.filter(r => !matches(r));
      return [...prev, { ...rec, patientCard }];
    });
  };

  const updateRecommField = (rec, field, value) => {
    isDirtyRef.current = true;
    setSelectedRecomms(prev => prev.map(r => {
      const isMatch = (r._id && rec._id && r._id === rec._id) || r.recommendation === rec.recommendation;
      return isMatch ? { ...r, [field]: value } : r;
    }));
  };

  /* ── Generate Treatment Plan — auto-suggest from mappings when slate is empty ── */
  const [gtpError, setGtpError] = useState(false);

  const handleGeneratePlan = async () => {
    if (!patientCard) return;
    // Require at least one body part with symptoms before opening GTP
    const partsWithSymptoms = Object.entries(bodySymptoms).filter(
      ([, s]) => Object.values(s || {}).some(v => v > 0)
    );
    if (partsWithSymptoms.length === 0) {
      showToast('Please select body parts and fill symptoms before generating a treatment plan.', 'error');
      return;
    }
    // Check all incomplete parts (have exercises but missing symptoms, or vice versa)
    const incompleteParts = Object.entries(bodyPartStatus)
      .filter(([, st]) => st === 'incomplete');
    if (incompleteParts.length > 0) {
      // Find which ones are missing symptoms specifically
      const missingSymptoms = incompleteParts.filter(([k]) => {
        const s = bodySymptoms[k] || {};
        return !Object.values(s).some(v => v > 0);
      });
      if (missingSymptoms.length > 0) {
        const labels = missingSymptoms.map(([k]) => {
          const cfg = [...(bodyPartsConfig.front || []), ...(bodyPartsConfig.back || [])].find(p => p.key === k);
          return cfg?.label || k;
        });
        setSelectedPart(missingSymptoms[0][0]);
        showToast(`Please add symptoms for: ${labels.join(', ')}`, 'error');
        return;
      }
    }
    setGtpError(false);

    // Auto-suggest from Mappings only when therapist hasn't added anything yet
    if (selectedSeqs.length === 0 && selectedRecomms.length === 0) {
      try {
        // Derive unique body-part schema keys from active body symptoms
        const uniqueParts = [...new Set(
          Object.keys(bodySymptoms)
            .filter(k => Object.values(bodySymptoms[k] || {}).some(v => v > 0))
            .map(k => PART_TO_SCHEMA_SINGLE[k] || PART_TO_SCHEMA_ARRAY[k] || k)
        )].filter(Boolean);

        if (uniqueParts.length > 0) {
          const res = await api.get(
            `${process.env.REACT_APP_URL}/mappings/suggest?bodyParts=${uniqueParts.join(',')}`
          );
          const sug = res.data || {};
          if (sug.sequences?.length)       setSelectedSeqs(sug.sequences.map(s => ({ ...s, patientCard })));
          if (sug.recommendations?.length) setSelectedRecomms(sug.recommendations.map(r => ({ ...r, patientCard })));
        }
      } catch (_) {
        // fail silently — therapist can add manually
      }
    }

    setShowPlanModal(true);
  };

  const handleEscalateGtp = async () => {
    try {
      const patientName = profile?.name || patientCard;
      await api.post('/tasks', { task: {
        title: `GTP needed — ${patientName} (${patientCard})`,
        notes: `Therapist ${auth.username} was unable to generate treatment plan for saadhak ${patientName} (${patientCard}). Please review and generate the GTP manually.`,
        assignee: 'owner',
        priority: 'high',
        estimatedEndDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
      }});
      await api.post('/notifications', {
        userId: 'owner',
        title: 'GTP Escalation — ' + patientName,
        message: `${auth.username} needs help generating treatment plan for ${patientName} (${patientCard}).`,
        type: 'ESCALATION',
      });
      setGtpError(false);
      alert('Escalated to owner. A task and notification have been sent.');
    } catch (err) {
      alert('Failed to escalate: ' + (err.response?.data?.message || err.message));
    }
  };

  /* ── Called by TreatmentPlanModal on "Submit for Approval" ── */
  const handlePlanSubmit = async (plan) => {
    try {
      // Build payload for active parts
      const consultPayload = buildConsultationPayload(bodySymptoms);

      // Compute explicit zeros for parts that were removed (in prevConsultParts, now absent, not excluded)
      const excludedSet = new Set(plan.planExcludedParts || []);
      const clearExtras = {};
      [...prevConsultParts].forEach(partKey => {
        if (bodySymptoms[partKey] || excludedSet.has(partKey)) return;
        const schemaField = PART_TO_SCHEMA_SINGLE[partKey] || PART_TO_SCHEMA_ARRAY[partKey];
        if (!schemaField || schemaField in consultPayload || schemaField in clearExtras) return;
        if (PART_TO_SCHEMA_SINGLE[partKey]) {
          clearExtras[schemaField] = { grade: "0", numbness: false, stiffnes: false, painside: "both", answers: { weakness:0, burning:0, spasm:0, instability:0 } };
        } else {
          clearExtras[schemaField] = [];
        }
      });

      await api.post(`${process.env.REACT_APP_URL}/patients/consultation/submit-approval`, {
        patientCard,
        patientName:      profile?.name || patientCard,
        therapistUsername: auth?.username || "",
        planStatus:        plan.planStatus || "pending_approval",
        // Zero out explicitly removed parts first, then overlay active parts
        ...clearExtras,
        ...consultPayload,
        recommendations:      plan.recommendations,   // use plan's final recommendations, not stale CIV state
        partComments,
        generalComments,
        // Plan-specific fields from modal
        sequences:            plan.sequences,
        mythsessions:         plan.mythsessions,
        maintsessions:        plan.maintsessions,
        preventivesessions:   plan.preventivesessions,
        drcomments:           plan.drcomments,
        planExcludedParts:    plan.planExcludedParts || [],
        planPartHolds:        plan.planPartHolds     || {},
        planRomLinkedSelections: plan.planRomLinkedSelections || {},
        planServices:         plan.planServices      || [],
        // Exact annotation-key round-trip: bodyParts[] for next-load restoration
        bodyParts: Object.entries(bodySymptoms).map(([partKey, symptoms]) => ({
          partKey,
          stype:    PART_TO_STYPE[partKey] || '',
          label:    [...bodyPartsConfig.front, ...bodyPartsConfig.back].find(p => p.key === partKey)?.label || partKey,
          symptoms: { ...symptoms },
        })),
      });
      localStorage.removeItem(LS_KEY(patientCard));
      isDirtyRef.current = false;
      setShowPlanModal(false);
      showAlert('Treatment Plan submitted successfully!', 'success');
      navigate('/ListPatients', { state: { data: { fromTab: 'CIV', toTab: 'LabReadingsView', pc: patientCard } } });
    } catch (err) {
      showAlert("Error submitting: " + (err.response?.data || err.message || "Unknown error"), 'error');
    }
  };

  /* ── MyNotes CRUD (owner/solution only) ── */
  const fetchMyNotes = async () => {
    if (!patientCard || !isOwnerOrSolution) return;
    setMyNotesLoading(true);
    try {
      const r = await api.get(`${process.env.REACT_APP_URL}/patients/mynotes/${patientCard}`);
      setMyNotes(r.data || []);
    } catch (e) { setMyNotes([]); }
    finally { setMyNotesLoading(false); }
  };
  const addMyNote = async () => {
    if (!newNoteText.trim()) return;
    try {
      const r = await api.post(`${process.env.REACT_APP_URL}/patients/mynotes/${patientCard}`, { text: newNoteText.trim() });
      setMyNotes(r.data || []);
      setNewNoteText('');
    } catch (e) { showAlert(e.response?.data?.error || 'Error adding note'); }
  };
  const updateMyNote = async (noteId) => {
    if (!editingNoteText.trim()) return;
    try {
      const r = await api.put(`${process.env.REACT_APP_URL}/patients/mynotes/${patientCard}/${noteId}`, { text: editingNoteText.trim() });
      setMyNotes(r.data || []);
      setEditingNoteId(null);
      setEditingNoteText('');
    } catch (e) { showAlert(e.response?.data?.error || 'Error updating note'); }
  };
  const deleteMyNote = async (noteId) => {
    if (!await showConfirm('Delete this note?', { okLabel: 'Delete', danger: true })) return;
    try {
      const r = await api.delete(`${process.env.REACT_APP_URL}/patients/mynotes/${patientCard}/${noteId}`);
      setMyNotes(r.data || []);
    } catch (e) { showAlert(e.response?.data?.error || 'Error deleting note'); }
  };

  /* ── Reset today's changes to previous consultation state ── */
  const handleResetConsultation = async () => {
    if (!patientCard) return;
    if (!await showConfirm("Reset all today's consultation changes and restore the previous consultation data?", { okLabel: 'Reset', cancelLabel: 'Cancel', danger: true })) return;
    setResetting(true);
    try {
      const res = await api.post(`${process.env.REACT_APP_URL}/patients/consultation/${patientCard}/reset-today`, {
        resetBy: auth?.username || "owner",
      });
      const snap = res.data?.snapshot;
      if (snap) {
        // Reload data from BE
        localStorage.removeItem(LS_KEY(patientCard));
        isDirtyRef.current = false;
        await loadPatientData(patientCard);
        showAlert("Consultation reset to previous state ✓", 'success');
      }
    } catch (err) {
      showAlert("Reset failed: " + (err.response?.data || err.message || "Unknown error"), 'error');
    } finally {
      setResetting(false);
    }
  };

  const handlePatientSelect = (p) => {
    setSearchInput(""); setSearchResults([]);
    setPatientCard(p.patientCard);
    loadPatientData(p.patientCard);
  };

  /* ── derived ──────────────────────────────────────────── */
  const overallPain = Object.values(bodySymptoms).reduce((m, s) => Math.max(m, s.pain || 0), 0);
  const pre = preConsult || {};
  const visitType = appointment?.type || appointment?.visitType || "Follow-Up";
  const selSymptoms = selectedPart ? (bodySymptoms[selectedPart] || {}) : {};
  const selPartLabel = [...bodyPartsConfig.front, ...bodyPartsConfig.back].find(p => p.key === selectedPart)?.label || selectedPart || "—";

  /* filter recommendations by selected body part's rtype */
  const selStype = selectedPart ? PART_TO_STYPE[selectedPart] : null;
  const filteredRecomms = allRecomms.filter(r =>
    !selStype || !r.rtype || r.rtype.toLowerCase() === selStype.toLowerCase()
  );


  /* ════════════════════════════════════════════════════════
     SEARCH SCREEN
  ════════════════════════════════════════════════════════ */
  if (!patientCard || (!profile && !loading && !error)) {
    return (
      <div className="civ-frame">
        <div className="civ-screen">
          <div className="civ-topbar">
            <div className="civ-patient-line">
              <div className="civ-avatar" />
              <span className="civ-strong" style={{ color: "#fff", fontSize: 16 }}>Consultation View — Search Saadhak</span>
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:14, marginTop:60 }}>
            <input type="text" list="civ-opts" value={searchInput}
              placeholder="Enter 3+ characters: Name / Card / Phone"
              onChange={e => setSearchInput(e.target.value)}
              onInput={e => {
                const m = searchResults.find(p => `${p.name} (${p.patientCard}) - ${p.number}` === e.target.value);
                if (m) handlePatientSelect(m);
              }}
              className="civ-search-input"
            />
            <datalist id="civ-opts">
              {[...new Set(searchResults.map(p => `${p.name} (${p.patientCard}) - ${p.number}`))].map((v, i) => (
                <option key={i} value={v} />
              ))}
            </datalist>
            {isSearching && <span style={{ color:"#888", fontSize:13 }}>Searching…</span>}
          </div>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="civ-frame"><div className="civ-screen civ-center"><span style={{ fontSize:18, color:"#555" }}>Loading…</span></div></div>
  );
  if (error) return (
    <div className="civ-frame"><div className="civ-screen civ-center" style={{ flexDirection:"column", gap:14 }}>
      <span style={{ color:"salmon" }}>{error}</span>
      <button className="civ-btn civ-btn-outline"
        onClick={() => { setPatientCard(""); setProfile(null); setError(""); }}>
        Try Another Saadhak
      </button>
    </div></div>
  );

  /* ════════════════════════════════════════════════════════
     MAIN VIEW
  ════════════════════════════════════════════════════════ */
  return (
    <div className="civ-frame">
      {dialogNode}
      {civToast && (
        <div style={{
          position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 99999,
          padding: '12px 24px', borderRadius: 10, fontSize: 14, fontWeight: 700, maxWidth: '90vw',
          background: civToast.type === 'error' ? '#fef2f2' : '#f0fdf4',
          color: civToast.type === 'error' ? '#dc2626' : '#16a34a',
          border: `1.5px solid ${civToast.type === 'error' ? '#fca5a5' : '#86efac'}`,
          boxShadow: '0 4px 20px rgba(0,0,0,.15)',
          animation: 'fadeIn .2s ease',
        }}>
          {civToast.message}
        </div>
      )}
      <div className="civ-screen">

        {/* ── BLUE HEADER ─────────────────────────────────── */}
        <div className="civ-topbar">
          <div className="civ-patient-line">
            <span className="civ-topbar-label">Saadhak:</span>
            <span className="civ-strong civ-white">
              {profile?.name || patientCard}
              {profile?.age ? ` (${profile.age} / ${profile.gender || "—"})` : ""}
            </span>
            <span className="civ-sep" />
            <span className="civ-topbar-label">Visit:</span>
            <span className="civ-strong civ-white">{visitType}</span>
            <span className="civ-sep" />
            <span className="civ-topbar-label">Pain:</span>
            <span className="civ-strong civ-white">{overallPain} / 10</span>
          </div>
          <div className="civ-topbar-right">
            <div className="civ-bell">🔔</div>
            <div className="civ-avatar" style={{
              background: profile?.gender === 'female' ? 'linear-gradient(135deg,#ec4899,#8b5cf6)' : profile?.gender === 'male' ? 'linear-gradient(135deg,#3b82f6,#0891b2)' : 'linear-gradient(135deg,#6b7280,#374151)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, lineHeight: 1,
            }}>
              {profile?.gender === 'female' ? '👩' : profile?.gender === 'male' ? '👨' : '🧑'}
            </div>
          </div>
        </div>

        {/* ── VITALS BAR ──────────────────────────────────── */}
        <div className="civ-vitals-bar">
          <VitalItem label="Height" value={pre.height ? `${pre.height} cm` : "—"} />
          <div className="civ-vdiv" />
          <VitalItem label="Weight" value={pre.weight ? `${pre.weight} kg` : "—"} />
          <div className="civ-vdiv" />
          <VitalItem label="BP" value={pre.bp ? `${pre.bp} mmHg` : "—"} />
          <div className="civ-vdiv" />
          <VitalItem label="Pulse" value={pre.pulse ? `${pre.pulse} bpm` : "—"} />
          <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
            <button className="civ-btn civ-btn-primary civ-btn-sm" onClick={() => setShowReviewModal(true)}>📊 Review</button>
            <button className="civ-btn civ-btn-primary civ-btn-sm" onClick={() => setShowReportsModal(true)}>🧾 Reports</button>
            <button className="civ-btn civ-btn-primary civ-btn-sm" onClick={() => setShowProgressModal(true)}>📈 Progress</button>
            <button className="civ-btn civ-btn-primary civ-btn-sm" onClick={() => setShowHistoryModal(true)}>📋 History</button>
            {isOwnerOrSolution && patientCard && (
              <button className="civ-btn civ-btn-primary civ-btn-sm" onClick={() => { setShowMyNotes(true); fetchMyNotes(); }} style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fbbf24' }}>📝 MyNotes</button>
            )}
            {isOwnerOrSolution && patientCard && (
              <button
                className="civ-btn-reset"
                disabled={resetting}
                onClick={handleResetConsultation}
                title="Reset today's changes to previous consultation state"
                style={{ height: 34, padding: "0 14px", fontSize: 12 }}
              >
                {resetting ? "Resetting…" : "↩ Reset"}
              </button>
            )}
          </div>
        </div>

        {/* ── INSTRUCTION ──────────────────────────────────── */}
        <div className="civ-instruction">
          Tap a region on the body map to select, then set symptom severity (1–10) and assign sequences.
        </div>

        {/* ── 3-COLUMN MAIN GRID ──────────────────────────── */}
        <div className="civ-main-grid">

          {/* ══ COL 1: BODY MAP + PROGRESS GRAPH ══ */}
          <div className="civ-col-left">
            <div className="civ-bodymap-card" style={{ padding: 0 }}>
              {/* ── Single body view (front or back) ── */}
              {(() => {
                const isFront  = bodyView === 'front';
                const zoom     = isFront ? frontZoom : backZoom;
                const setZoom  = isFront ? setFrontZoom : setBackZoom;
                const rot      = isFront ? frontRot  : backRot;
                const setRot   = isFront ? setFrontRot  : setBackRot;
                const panX     = isFront ? frontPanX : backPanX;
                const panY     = isFront ? frontPanY : backPanY;
                const setPanX  = isFront ? setFrontPanX : setBackPanX;
                const setPanY  = isFront ? setFrontPanY : setBackPanY;
                const parts    = isFront ? bodyPartsConfig.front : bodyPartsConfig.back;
                const imgSrc   = isFront ? '/front_body.png?v=2' : '/back_body.png?v=2';

                // Drag-to-pan handlers — only pan if pointer moves >5px, otherwise let click through
                const DRAG_THRESHOLD = 5;
                const onPointerDown = (e) => {
                  if (zoom <= 1.0) return;
                  dragRef.current = { startX: e.clientX, startY: e.clientY, origPanX: panX, origPanY: panY, didDrag: false, pid: e.pointerId, svg: e.currentTarget };
                };
                const onPointerMove = (e) => {
                  if (!dragRef.current) return;
                  const dx = e.clientX - dragRef.current.startX;
                  const dy = e.clientY - dragRef.current.startY;
                  if (!dragRef.current.didDrag && Math.abs(dx) + Math.abs(dy) < DRAG_THRESHOLD) return;
                  if (!dragRef.current.didDrag) {
                    dragRef.current.didDrag = true;
                    try { dragRef.current.svg.setPointerCapture(dragRef.current.pid); } catch (_) {}
                  }
                  setPanX(dragRef.current.origPanX + dx / zoom);
                  setPanY(dragRef.current.origPanY + dy / zoom);
                };
                const onPointerUp = () => { dragRef.current = null; };
                return (
                  <>
                    <div className="civ-bodymap" style={{ flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
                      {/* ── Space-travel overlay during flip ── */}
                      {isFlipping && (
                        <div className="civ-space-overlay">
                          {Array.from({ length: 20 }).map((_, i) => {
                            const angle = (i / 20) * 360;
                            const len   = 30 + Math.random() * 60;
                            const color = i % 3 === 0 ? 'rgba(180,120,255,0.85)' : i % 3 === 1 ? 'rgba(100,180,255,0.75)' : 'rgba(255,160,220,0.70)';
                            return (
                              <div key={i} className="civ-space-streak" style={{
                                width: len, marginTop: -0.75,
                                background: `linear-gradient(90deg, transparent, ${color})`,
                                transform: `rotate(${angle}deg)`,
                                animationDelay: `${(i % 5) * 60}ms`,
                              }} />
                            );
                          })}
                          {[{ s: 60, c: 'rgba(160,80,255,0.55)', d: '0ms'  },
                            { s: 100, c: 'rgba(80,160,255,0.40)', d: '120ms'},
                            { s: 36,  c: 'rgba(255,120,200,0.50)', d:'250ms'}].map((ring, i) => (
                            <div key={i} className="civ-space-ring" style={{
                              width: ring.s, height: ring.s,
                              marginTop: -(ring.s / 2), marginLeft: -(ring.s / 2),
                              borderColor: ring.c,
                              animationDelay: ring.d,
                            }} />
                          ))}
                          <div className="civ-space-orb" style={{ width: 28, height: 28 }} />
                          <div style={{ position: 'absolute', bottom: 14, left: 0, right: 0, textAlign: 'center', color: 'rgba(200,160,255,0.90)', fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                            {bodyView === 'front' ? '← Back View' : 'Front View →'}
                          </div>
                        </div>
                      )}
                      <div className="civ-body-view" style={{
                          width: '100%', height: '100%', position: 'relative',
                          animation: isFlipping
                            ? `civ-flip-${isFlipping}-${flipDir === 1 ? 'r' : 'l'} 650ms ${isFlipping === 'out' ? 'ease-in' : 'ease-out'} forwards`
                            : 'none',
                        }}>
                        <svg
                          viewBox="0 0 390 520"
                          width="100%" height="100%"
                          preserveAspectRatio="xMidYMid slice"
                          style={{ display: 'block', width: '100%', height: '100%', cursor: zoom > 1 ? 'grab' : 'default', touchAction: 'none' }}
                          onPointerDown={onPointerDown}
                          onPointerMove={onPointerMove}
                          onPointerUp={onPointerUp}
                          onPointerCancel={onPointerUp}
                        >
                          {/* Image + dots share the same transform so zoom/pan/rotate applies to both */}
                          <g transform={`translate(${195 + panX},${260 + panY}) rotate(${rot}) scale(${zoom}) translate(-195,-260)`}>
                            <image
                              href={imgSrc}
                              x="0" y="0"
                              width="390" height="520"
                              preserveAspectRatio="xMidYMid meet"
                            />
                            {parts.filter(p => p.type !== 'line').map(part => (
                              <PainDot key={part.key} part={part}
                                symptoms={bodySymptoms[part.key]}
                                isSelected={selectedPart === part.key}
                                readiness={bodyPartStatus[part.key]}
                                hasPrevData={prevConsultParts.has(part.key)}
                                isExcluded={planExcludedParts.includes(part.key)}
                                isPlanHeld={!!(planPartHolds[part.key]?.held) && !!bodySymptoms[part.key] && !planExcludedParts.includes(part.key)}
                                onClick={() => handleBodyPartClick(part.key)}
                              />
                            ))}
                            {parts.filter(p => p.type === 'line').map(part => (
                              <PainLine key={part.key} part={part}
                                symptoms={bodySymptoms[part.key]}
                                readiness={bodyPartStatus[part.key]}
                                isSelected={selectedPart === part.key}
                                hasPrevData={prevConsultParts.has(part.key)}
                                isExcluded={planExcludedParts.includes(part.key)}
                                isPlanHeld={!!(planPartHolds[part.key]?.held) && !!bodySymptoms[part.key] && !planExcludedParts.includes(part.key)}
                                onClick={() => handleBodyPartClick(part.key)}
                              />
                            ))}
                          </g>
                        </svg>
                      </div>
                    </div>

                  </>
                );
              })()}

              {/* Severity legend */}
              <div className="civ-legend-row" style={{ padding: '5px 10px' }}>
                <span className="civ-legend-item"><span className="civ-dot" style={{ background:"rgba(210,55,45,0.82)" }} />Severe (7–10)</span>
                <span className="civ-legend-item"><span className="civ-dot" style={{ background:"rgba(220,128,25,0.82)" }} />Moderate (4–6)</span>
                <span className="civ-legend-item"><span className="civ-dot" style={{ background:"rgba(170,190,55,0.78)" }} />Mild (1–3)</span>
                <span className="civ-legend-item"><span className="civ-dot" style={{ background:"rgba(160,175,200,0.35)" }} />None</span>
              </div>

              {/* ── Front/Back toggle + Zoom controls ── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 10px', margin: '4px 0 2px', flexWrap: 'wrap', gap: 6 }}>
                {/* Front / Back toggle */}
                <div style={{ display: 'flex', gap: 6 }}>
                  {[{ v: 'front', label: 'Front' }, { v: 'back', label: 'Back' }].map(({ v, label }) => {
                    const active = bodyView === v;
                    return (
                      <button key={v} type="button" onClick={() => handleBodyViewToggle(v)} style={{
                        padding: '3px 20px', fontSize: 12, fontWeight: active ? 700 : 500,
                        border: '1px solid ' + (active ? '#6366f1' : '#d1d5db'),
                        background: active ? '#e0e7ff' : '#fff',
                        color: active ? '#4338ca' : '#6b7280',
                        borderRadius: 20, cursor: 'pointer',
                      }}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                {/* Zoom + Pan controls */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button type="button" style={bvBtn} onClick={() => { const isFront = bodyView === 'front'; const setZ = isFront ? setFrontZoom : setBackZoom; setZ(z => Math.max(1.0, +(z - 0.05).toFixed(2))); }}>−</button>
                  <span style={bvVal}>{Math.round((bodyView === 'front' ? frontZoom : backZoom) * 100)}%</span>
                  <button type="button" style={bvBtn} onClick={() => { const isFront = bodyView === 'front'; const setZ = isFront ? setFrontZoom : setBackZoom; setZ(z => Math.min(3.5, +(z + 0.05).toFixed(2))); }}>+</button>
                  <span style={{ width: 1, height: 16, background: '#d1d5db', margin: '0 2px' }} />
                  <button type="button" title="Pan up" style={bvBtn} onClick={() => { if (bodyView === 'front') setFrontPanY(v => v + 25); else setBackPanY(v => v + 25); }}>↑</button>
                  <button type="button" title="Pan down" style={bvBtn} onClick={() => { if (bodyView === 'front') setFrontPanY(v => v - 25); else setBackPanY(v => v - 25); }}>↓</button>
                  <button type="button" title="Pan left" style={bvBtn} onClick={() => { if (bodyView === 'front') setFrontPanX(v => v + 25); else setBackPanX(v => v + 25); }}>←</button>
                  <button type="button" title="Pan right" style={bvBtn} onClick={() => { if (bodyView === 'front') setFrontPanX(v => v - 25); else setBackPanX(v => v - 25); }}>→</button>
                  <span style={{ width: 1, height: 16, background: '#d1d5db', margin: '0 2px' }} />
                  <button type="button" title="Reset zoom & position" style={{ ...bvBtn, marginLeft: 2, fontSize: 12, padding: '2px 5px' }}
                    onClick={() => { if (bodyView === 'front') { setFrontZoom(DEFAULT_ZOOM); setFrontRot(0); setFrontPanX(0); setFrontPanY(0); } else { setBackZoom(DEFAULT_ZOOM); setBackRot(0); setBackPanX(0); setBackPanY(0); } }}>⊕</button>
                </div>
              </div>
            </div>

            {/* REGION SYMPTOM CHARTS — one per affected body region, all symptoms as bars */}
            <div className="civ-progress-card" style={{ marginTop: 14 }}>
              <div className="civ-progress-header">
                <span className="civ-progress-title">Region Charts</span>
                <span style={{ fontSize:10, color:"#aaa" }}>Tap a region above to select it</span>
              </div>
              {Object.keys(bodySymptoms).length === 0 && (
                <div style={{ color:'#ccc', fontSize:11, textAlign:'center', padding:'20px 0', fontStyle:'italic' }}>
                  Select body regions to see symptom charts
                </div>
              )}
              {Object.entries(bodySymptoms).filter(([, s]) => SYMPTOM_LIST.some(sym => (s[sym.key] || 0) > 0)).map(([partKey, s]) => {
                const partLabel = [...bodyPartsConfig.front, ...bodyPartsConfig.back].find(p => p.key === partKey)?.label || partKey;
                const W = 340, H = 104, PL = 6, PR = 6, PT = 14, PB = 26;
                const nBars = SYMPTOM_LIST.length;
                const totalW = W - PL - PR;
                const barSlot = totalW / nBars;
                const barW = barSlot * 0.60;
                const barOff = barSlot * 0.20;
                const maxBarH = H - PT - PB;
                const barH = v => maxBarH * (v / 10);
                const isSelected = selectedPart === partKey;
                const rdns = bodyPartStatus[partKey]; // 'ready' | 'incomplete' | undefined
                const dotColor = rdns === 'ready' ? '#3b82f6' : rdns === 'incomplete' ? '#ec4899' : '#aaa';
                return (
                  <div key={partKey} style={{ marginBottom:12, background: rdns === 'ready' ? 'rgba(59,130,246,0.07)' : rdns === 'incomplete' ? 'rgba(236,72,153,0.07)' : 'transparent', borderRadius:8, padding:'6px 4px', cursor:'pointer', transition:'background 0.15s', border: isSelected ? '1px solid rgba(59,130,246,0.25)' : rdns === 'ready' ? '1px solid rgba(59,130,246,0.15)' : rdns === 'incomplete' ? '1px solid rgba(236,72,153,0.20)' : '1px solid transparent' }}
                    onClick={() => handleBodyPartClick(partKey)}>
                    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:4, paddingLeft:4 }}>
                      <span style={{ width:9, height:9, borderRadius:'50%', background: dotColor, flexShrink:0 }} />
                      <span style={{ fontSize:13, fontWeight:700, color: dotColor }}>{partLabel}</span>
                      {!SYMPTOM_LIST.some(sym => (s[sym.key] || 0) > 0) && (
                        <span style={{ fontSize:10, color:'#ccc', fontStyle:'italic', marginLeft:4 }}>no scores</span>
                      )}
                    </div>
                    <svg width="100%" viewBox={`0 0 ${W} ${H}`} className="civ-graph-svg">
                      {[0,5,10].map(v => (
                        <line key={v} x1={PL} y1={H - PB - maxBarH*(v/10)} x2={W-PR} y2={H - PB - maxBarH*(v/10)}
                          stroke="rgba(0,0,0,0.06)" strokeWidth="1"/>
                      ))}
                      {/* Y-axis scale labels */}
                      {[0,5,10].map(v => (
                        <text key={v} x={PL+1} y={H - PB - maxBarH*(v/10) - 1} fontSize="8" fill="rgba(0,0,0,0.22)" textAnchor="start">{v}</text>
                      ))}
                      {SYMPTOM_LIST.map((sym, i) => {
                        const val = s[sym.key] || 0;
                        const x = PL + i * barSlot + barOff;
                        const bh = Math.max(barH(val), val > 0 ? 4 : 0);
                        const y = H - PB - bh;
                        return (
                          <g key={sym.key}>
                            <rect x={x} y={val > 0 ? y : H - PB - 1} width={barW} height={Math.max(bh, 1)}
                              fill={val > 0 ? sym.color : 'rgba(200,210,220,0.25)'} rx="2" opacity={val > 0 ? 0.88 : 0.4}/>
                            {val > 0 && (
                              <text x={x+barW/2} y={y-2} fontSize="10" fill={sym.color} textAnchor="middle" fontWeight="800">{val}</text>
                            )}
                            <text x={x+barW/2} y={H-3} fontSize="9" fill={val > 0 ? "#777" : "#ccc"} textAnchor="middle" fontWeight={val > 0 ? "600" : "400"}>{sym.label.slice(0,4)}</text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>
                );
              })}
              {/* Overall session pain trend (when session history exists) */}
              {(symptomGraphData['pain'] || []).length >= 2 && (
                <div style={{ marginTop:8, paddingTop:8, borderTop:'1px dashed rgba(0,0,0,0.07)' }}>
                  <div style={{ fontSize:10, color:'#aaa', marginBottom:3 }}>Overall pain trend (sessions)</div>
                  {(() => {
                    const pts = symptomGraphData['pain'];
                    const W2 = 320, H2 = 52, PL2 = 18, PT2 = 5, PB2 = 14;
                    const xSt = (W2 - PL2) / Math.max(pts.length - 1, 1);
                    const yV2 = v => PT2 + (H2 - PT2 - PB2) * (1 - v / 10);
                    const polyPts = pts.map((d,i) => `${PL2 + i*xSt},${yV2(d.value)}`).join(" ");
                    const trend = pts[pts.length-1].value < pts[0].value ? '↓ improving' : pts[pts.length-1].value > pts[0].value ? '↑ worsening' : '→ stable';
                    return (
                      <svg width="100%" viewBox={`0 0 ${W2} ${H2}`} className="civ-graph-svg">
                        {[0,5,10].map(v => (
                          <line key={v} x1={PL2} y1={yV2(v)} x2={W2} y2={yV2(v)} stroke="rgba(0,0,0,0.05)" strokeWidth="1"/>
                        ))}
                        {[0,5,10].map(v => (
                          <text key={v} x={PL2-3} y={yV2(v)+3} fontSize="7" fill="#ccc" textAnchor="end">{v}</text>
                        ))}
                        <polyline points={polyPts} fill="none" stroke="#4a9d5e" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round"/>
                        {pts.map((d,i) => (
                          <g key={i}>
                            <circle cx={PL2+i*xSt} cy={yV2(d.value)} r="3" fill="#4a9d5e"/>
                            <text x={PL2+i*xSt} y={H2-1} fontSize="7" fill="#bbb" textAnchor="middle">{d.label}</text>
                          </g>
                        ))}
                        <text x={W2-2} y={PT2+8} fontSize="8" fill="#4a9d5e" textAnchor="end" fontStyle="italic">{trend}</text>
                      </svg>
                    );
                  })()}
                </div>
              )}

              {/* Per-body-part ROM & MVC progress (from session data) */}
              {(Object.keys(romProgressData).length > 0 || Object.keys(mvcProgressData).length > 0) && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed rgba(0,0,0,0.07)' }}>
                  <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>ROM & MVC Progress by Body Part</div>
                  {[...new Set([...Object.keys(romProgressData), ...Object.keys(mvcProgressData)])].filter(partKey => bodySymptoms[partKey] && SYMPTOM_LIST.some(sym => (bodySymptoms[partKey][sym.key] || 0) > 0)).map(partKey => {
                    const partLabel = [...bodyPartsConfig.front, ...bodyPartsConfig.back].find(p => p.key === partKey)?.label || partKey;
                    const romPts = romProgressData[partKey] || [];
                    const mvcPts = mvcProgressData[partKey] || [];
                    const W3 = 300, H3 = 40, PL3 = 10, PT3 = 4, PB3 = 10;
                    const nPts = Math.max(romPts.length, mvcPts.length, 2);
                    const xSt3 = (W3 - PL3) / Math.max(nPts - 1, 1);
                    const romMax = Math.max(...romPts.map(p => p.value), 1);
                    const mvcMax = 5;
                    const yRom = v => PT3 + (H3 - PT3 - PB3) * (1 - v / Math.max(romMax, 1));
                    const yMvc = v => PT3 + (H3 - PT3 - PB3) * (1 - v / mvcMax);
                    return (
                      <div key={partKey} style={{ marginBottom: 8, cursor: 'pointer', borderRadius: 6, padding: '4px', background: bodyPartStatus[partKey] === 'ready' ? 'rgba(59,130,246,0.06)' : bodyPartStatus[partKey] === 'incomplete' ? 'rgba(236,72,153,0.06)' : 'transparent' }} onClick={() => handleBodyPartClick(partKey)}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: bodyPartStatus[partKey] === 'ready' ? '#3b82f6' : bodyPartStatus[partKey] === 'incomplete' ? '#ec4899' : '#374151', marginBottom: 2 }}>{partLabel}</div>
                        <svg width="100%" viewBox={`0 0 ${W3} ${H3}`} className="civ-graph-svg">
                          {romPts.length >= 2 && (
                            <polyline points={romPts.map((d, i) => `${PL3 + i * xSt3},${yRom(d.value)}`).join(" ")}
                              fill="none" stroke="#60a5fa" strokeWidth="1.5" strokeLinejoin="round" />
                          )}
                          {mvcPts.length >= 2 && (
                            <polyline points={mvcPts.map((d, i) => `${PL3 + i * xSt3},${yMvc(d.value)}`).join(" ")}
                              fill="none" stroke="#34d399" strokeWidth="1.5" strokeLinejoin="round" />
                          )}
                          {romPts.map((d, i) => (
                            <circle key={i} cx={PL3 + i * xSt3} cy={yRom(d.value)} r="2" fill="#60a5fa" />
                          ))}
                          {mvcPts.map((d, i) => (
                            <circle key={i} cx={PL3 + i * xSt3} cy={yMvc(d.value)} r="2" fill="#34d399" />
                          ))}
                        </svg>
                        <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                          {romPts.length > 0 && <span style={{ fontSize: 8, color: '#60a5fa' }}>■ ROM (°)</span>}
                          {mvcPts.length > 0 && <span style={{ fontSize: 8, color: '#34d399' }}>■ MVC (0-5)</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* ══ COL 2: SYMPTOM PANEL + RECOMMENDATIONS ══ */}
          <div className="civ-col-mid">
            <div className="civ-symptom-panel">
              {selectedPart ? (
                <>
                  <div className="civ-symptom-header">
                    <span className="civ-symptom-title">{selPartLabel}</span>
                    <button
                      onClick={() => setShowVideoModal(true)}
                      title="Assign related videos to this patient"
                      style={{
                        marginLeft: 'auto', marginRight: 6,
                        padding: '3px 10px', borderRadius: 8,
                        border: '1.5px solid #4f46e5', background: '#eef2ff',
                        color: '#4f46e5', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                      }}
                    >
                      📹 Videos
                    </button>
                    {/* Remove entire body part from this consultation — opens confirmation modal */}
                    {bodySymptoms[selectedPart] && (
                      <button
                        title={`Remove ${selPartLabel} from this consultation`}
                        style={{
                          marginRight: 6, padding: '3px 10px', borderRadius: 8,
                          border: '1.5px solid #dc2626', background: '#fef2f2',
                          color: '#dc2626', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                        }}
                        onClick={() => handleRemovePartRequest(selectedPart)}
                      >🗑️ Remove Part</button>
                    )}
                    <button className="civ-x-btn" onClick={() => setSelectedPart(null)}>✕</button>
                  </div>
                  <div className="civ-symptom-list">
                    {SYMPTOM_LIST.map(sym => {
                      const score = selSymptoms[sym.key] || 0;
                      return (
                        <div key={sym.key} className="civ-sym-row">
                          <div className="civ-sym-icon" style={{ background: sym.color }}>
                            <span style={{ color:"#fff", fontSize:10, fontWeight:700 }}>{sym.icon}</span>
                          </div>
                          <span className="civ-sym-name">{sym.label}</span>
                          <div className="civ-score-row">
                            {SCORE_OPTS.map(n => (
                              <button key={n}
                                className={`civ-score-btn${score === n ? " active" : ""}`}
                                onClick={() => setSymptomScore(selectedPart, sym.key, n)}
                              >{n}</button>
                            ))}
                            {score > 0 && (
                              <button className="civ-score-btn civ-score-clr"
                                onClick={() => setSymptomScore(selectedPart, sym.key, 0)}
                                title="Clear this symptom">×</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="civ-quick-row">
                    <span className="civ-quick-link">+ Quick Add Symptom</span>
                    <button className="civ-btn civ-btn-primary civ-btn-sm">Add</button>
                  </div>
                  {/* Stype badge */}
                  {PART_TO_STYPE[selectedPart] && (
                    <div className="civ-stype-badge">
                      Region: <strong>{PART_TO_STYPE[selectedPart]}</strong>
                    </div>
                  )}
                  {/* Patient problems — history + today's input */}
                  {(() => {
                    const partHistory = (consultation?.reviewHistory || [])
                      .slice().reverse()
                      .filter(e => e.partComments?.[selectedPart])
                      .slice(0, 2);
                    return (
                      <div style={{ margin: "8px 2px 4px", paddingBottom: 4 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>
                          Patient's Problems
                        </div>
                        {/* Previous comments — last 2 */}
                        {partHistory.map((entry, i) => (
                          <div key={i} style={{ background: '#f5f3ff', border: '1px solid #e9d5ff', borderRadius: 7, padding: '5px 9px', marginBottom: 5, fontSize: 11.5 }}>
                            <span style={{ color: '#7c3aed', fontWeight: 700, marginRight: 6 }}>
                              {new Date(entry.date).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })}
                              {entry.reviewedBy ? ` · ${entry.reviewedBy}` : ''}:
                            </span>
                            <span style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{entry.partComments[selectedPart]}</span>
                          </div>
                        ))}
                        {/* Today's input */}
                        <textarea
                          rows={5}
                          placeholder="Today's notes — specific problems, complaints, and history for this area…"
                          value={partComments[selectedPart] || ""}
                          onChange={e => {
                            isDirtyRef.current = true;
                            const val = e.target.value;
                            setPartComments(prev => ({ ...prev, [selectedPart]: val }));
                          }}
                          style={{ width: "100%", borderRadius: 8, border: "1.5px solid #c4b5fd", padding: "7px 10px", fontSize: 12, color: "#374151", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", background: "#fdfcff", lineHeight: 1.5 }}
                        />
                      </div>
                    );
                  })()}
                </>
              ) : (
                <div className="civ-no-part">
                  <div className="civ-no-part-icon">👆</div>
                  <div className="civ-no-part-text">Tap a body region to view and set symptoms</div>
                </div>
              )}
            </div>

            {/* Recommendations sub-panel — always visible */}
            <div className="civ-recomm-card">
              <div className="civ-recomm-header">
                <span>Recommendations</span>
                {selectedRecomms.length > 0 && (
                  <span className="civ-seq-count">{selectedRecomms.length} selected</span>
                )}
              </div>
              <div className="civ-recomm-list">
                {allRecomms.length === 0 && (
                  <div className="civ-recomm-empty">No recommendations in system</div>
                )}
                {allRecomms.length > 0 && filteredRecomms.length === 0 && selStype && (
                  <div className="civ-recomm-notice">
                    No recommendations for <strong>{selStype}</strong> — showing all:
                  </div>
                )}
                {(filteredRecomms.length > 0 ? filteredRecomms : allRecomms).map((r, i) => {
                  const matchFn = s => (s._id && s._id === r._id) || s.recommendation === r.recommendation;
                  const chosenRec = selectedRecomms.find(matchFn);
                  const isChosen = !!chosenRec;
                  return (
                    <div key={r._id || i} className={`civ-recomm-row${isChosen ? " chosen" : ""}`}>
                      <label className="civ-recomm-check">
                        <input type="checkbox" checked={isChosen} onChange={() => toggleRecomm(r)} />
                        <div className="civ-recomm-info">
                          <span className="civ-recomm-name">{r.recommendation}</span>
                          {r.rtype && <span className="civ-recomm-meta">{r.rtype}</span>}
                        </div>
                      </label>
                      {isChosen && (
                        <div className="civ-recomm-edit">
                          <label className="civ-recomm-edit-field">
                            <span>Qty</span>
                            <input type="number" min="0" placeholder="—"
                              value={chosenRec.qty || ""}
                              onChange={e => updateRecommField(r, "qty", e.target.value)} />
                          </label>
                          <label className="civ-recomm-edit-field">
                            <span>×/day</span>
                            <input type="number" min="0" placeholder="—"
                              value={chosenRec.not || ""}
                              onChange={e => updateRecommField(r, "not", Number(e.target.value))} />
                          </label>
                          <label className="civ-recomm-edit-field">
                            <span>Days</span>
                            <input type="number" min="0" placeholder="—"
                              value={chosenRec.nod || ""}
                              onChange={e => updateRecommField(r, "nod", Number(e.target.value))} />
                          </label>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* General Comments — always visible */}
            <div className="civ-recomm-card" style={{ marginTop: 8 }}>
              <div className="civ-recomm-header">
                <span>General Comments</span>
              </div>
              <div style={{ padding: "8px 10px" }}>
                {/* Previous general comments — last 2 */}
                {(consultation?.reviewHistory || [])
                  .slice().reverse()
                  .filter(e => e.generalComments)
                  .slice(0, 2)
                  .map((entry, i) => (
                    <div key={i} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '5px 9px', marginBottom: 5, fontSize: 11.5 }}>
                      <span style={{ color: '#15803d', fontWeight: 700, marginRight: 6 }}>
                        {new Date(entry.date).toLocaleDateString('en-GB', { day:'2-digit', month:'short' })}
                        {entry.reviewedBy ? ` · ${entry.reviewedBy}` : ''}:
                      </span>
                      <span style={{ color: '#374151', whiteSpace: 'pre-wrap' }}>{entry.generalComments}</span>
                    </div>
                  ))
                }
                {/* Today's input */}
                <textarea
                  rows={5}
                  placeholder="Today's general comments — session, treatment, patient behaviour…"
                  value={generalComments}
                  onChange={e => {
                    isDirtyRef.current = true;
                    setGeneralComments(e.target.value);
                  }}
                  style={{ width: "100%", borderRadius: 8, border: "1.5px solid #86efac", padding: "7px 10px", fontSize: 12, color: "#374151", resize: "vertical", fontFamily: "inherit", boxSizing: "border-box", background: "#f0fdf4", lineHeight: 1.5 }}
                />
              </div>
            </div>
          </div>

          {/* ══ COL 3: SEQUENCES PANEL ══ */}
          <div className="civ-col-right">
            <div className="civ-seq-panel">
              <div className="civ-seq-header">
                <span className="civ-seq-title">
                  {selectedPart
                    ? `Sequences · ${PART_TO_STYPE[selectedPart] || selPartLabel}`
                    : "Sequences"}
                </span>
                {selectedSeqs.length > 0 && (
                  <span className="civ-seq-count">{selectedSeqs.length} selected</span>
                )}
              </div>

              {/* ── Search — shown whenever there is anything to search ── */}
              {(selectedSeqs.length > 0 || (selectedPart && !seqsLoading && availableSeqs.length > 0)) && (
                <div className="civ-seq-search-row">
                  <input
                    type="text"
                    className="civ-seq-search"
                    placeholder="Search sequences…"
                    value={seqSearch}
                    onChange={e => setSeqSearch(e.target.value)}
                  />
                </div>
              )}

              {!selectedPart && selectedSeqs.length === 0 && (
                <div className="civ-seq-empty">Select a body region to see recommended sequences</div>
              )}

              {selectedPart && seqsLoading && (
                <div className="civ-seq-empty">Loading sequences…</div>
              )}

              {/* ── Unified list: selected first, then available ── */}
              <div className="civ-seq-list">

                {/* Selected sequences — always shown at top, expanded */}
                {selectedSeqs
                  .filter(seq => !seqSearch || seq.name?.toLowerCase().includes(seqSearch.toLowerCase()))
                  .map((seq, idx) => {
                    const ms = secsToMS(normalizeDur(seq.duration || 60, seq.durationUnit));
                    const seqKey = seq._id || seq.name;
                    return (
                      <div key={seqKey || idx} className="civ-seq-row chosen" style={seq.held ? { opacity: 0.65 } : undefined}>
                        <label className="civ-seq-check-label">
                          <input type="checkbox" checked={true}
                            onChange={() => toggleSeqSelection(seq)} />
                          <span className="civ-seq-name">
                            {seq.held && <span style={{ fontSize: 10, color: "#d97706", marginRight: 3 }}>🔒</span>}
                            {seq.name || "—"}
                          </span>
                        </label>
                        <div className="civ-seq-fields">
                          <div className="civ-dur-rep-row">
                            <input type="number" min="0" max="999" className="civ-hms-box"
                              placeholder="0" value={ms.m}
                              onChange={e => updateSeqField(seqKey, "duration", msToSecs(e.target.value, ms.s))}
                            />
                            <span className="civ-dur-lbl">min</span>
                            <span className="civ-hms-sep">:</span>
                            <input type="number" min="0" max="59" className="civ-hms-box"
                              placeholder="00" value={ms.s}
                              onChange={e => updateSeqField(seqKey, "duration", msToSecs(ms.m, e.target.value))}
                            />
                            <span className="civ-dur-lbl">sec</span>
                            <span className="civ-reps-lbl">×</span>
                            <input type="number" min="1" max="999" className="civ-reps-box"
                              value={seq.repetition || 1}
                              onChange={e => updateSeqField(seqKey, "repetition", Number(e.target.value))}
                            />
                            <span className="civ-dur-lbl">reps</span>
                            {seq.vlink && (
                              <a href={seq.vlink} target="_blank" rel="noopener noreferrer"
                                style={{ color: "#6366f1", fontSize: 14, textDecoration: "none", flexShrink: 0 }}
                                title="Watch exercise video">▶</a>
                            )}
                          </div>
                          <textarea className="civ-seq-remarks" rows={1}
                            placeholder="Remarks…"
                            value={seq.remarks || ""}
                            onChange={e => updateSeqField(seqKey, "remarks", e.target.value)}
                          />
                        </div>
                      </div>
                    );
                  })}

                {/* Divider between selected and available */}
                {selectedSeqs.length > 0 && selectedPart && !seqsLoading && availableSeqs.length > 0 && (
                  <div className="civ-seq-divider">
                    Available sequences — add more
                  </div>
                )}

                {selectedPart && !seqsLoading && !selectedBodyPartConfig && (
                  <div className="civ-seq-empty" style={{ fontSize: 11 }}>
                    Selected body region was not found in backend bodyparts config
                  </div>
                )}

                {/* Available (unselected) sequences fetched for clicked body regions */}
                {selectedPart && selectedBodyPartConfig && !seqsLoading && (() => {
                  const unselected = availableSeqs
                    .filter(seq => !selectedSeqs.find(s => (s._id && s._id === seq._id) || s.name === seq.name))
                    .filter(seq => !seqSearch || seq.name?.toLowerCase().includes(seqSearch.toLowerCase()));
                  if (availableSeqs.length > 0 && unselected.length === 0 && !seqSearch) {
                    return (
                      <div className="civ-seq-empty" style={{ fontSize: 11 }}>
                        All sequences for this region are already selected
                      </div>
                    );
                  }
                  return unselected.map((seq, idx) => (
                    <div key={seq._id || idx} className="civ-seq-row">
                      <label className="civ-seq-check-label">
                        <input type="checkbox" checked={false}
                          onChange={() => toggleSeqSelection(seq)} />
                        <span className="civ-seq-name">{seq.name || "—"}</span>
                      </label>
                    </div>
                  ));
                })()}

                {/* No body part selected but have selections */}
                {!selectedPart && selectedSeqs.length > 0 && (
                  <div className="civ-seq-empty" style={{ fontSize: 11, color: '#888', borderTop: '1px dashed rgba(0,0,0,0.08)', paddingTop: 10, marginTop: 6 }}>
                    Select a body region to add more sequences
                  </div>
                )}

                {selectedPart && selectedBodyPartConfig && !seqsLoading && availableSeqs.length === 0 && selectedSeqs.length === 0 && (
                  <div className="civ-seq-empty">No sequences found for this region</div>
                )}
              </div>

              {seqSaveMsg && (
                <div className="civ-seq-footer">
                  <span className="civ-seq-msg">{seqSaveMsg}</span>
                </div>
              )}
            </div>
          </div>

        </div>

        {/* ── BOTTOM BAR ───────────────────────────────────── */}
        <div className="civ-bottom-bar">
          <button
            className="civ-btn-generate"
            onClick={handleGeneratePlan}
          >
            Generate Treatment Plan
          </button>
          {patientCard && (
            <button
              onClick={() => { if (window.confirm('Need Help with GTP generation from Doctor ?')) handleEscalateGtp(); }}
              style={{
                padding: '10px 16px', borderRadius: 10, border: '1.5px solid #ef5350',
                background: '#fff', color: '#ef5350', fontWeight: 700, fontSize: '0.85em',
                cursor: 'pointer',
              }}
            >
              Help from Doctor ?
            </button>
          )}
          {patientCard && (
            <button
              onClick={() => {
                setShowOldGtpModal(true);
                if (oldGtpAllSvcs.length === 0) {
                  api.get(process.env.REACT_APP_URL + '/services')
                    .then(r => setOldGtpAllSvcs((r.data || []).filter(s => s.org === 'MedicalyogaTherapyCentre' && !s.parentId)))
                    .catch(() => {});
                }
              }}
              style={{
                padding: '10px 16px', borderRadius: 10, border: '1.5px solid #d97706',
                background: '#fffbeb', color: '#92400e', fontWeight: 700, fontSize: '0.85em',
                cursor: 'pointer',
              }}
            >
              OLD GTP Update
            </button>
          )}
        </div>

      </div>

      {showVideoModal && selectedPart && (
        <VideoAssignModal
          patientCard={patientCard}
          bodyPart={selectedPart}
          onClose={() => setShowVideoModal(false)}
        />
      )}
      {showProgressModal && patientCard && (
        <PatientProgressModal
          patientCard={patientCard}
          patientName={profile?.name}
          sessions={allSessions}
          selectedRecomms={selectedRecomms}
          bodySymptoms={bodySymptoms}
          onClose={() => setShowProgressModal(false)}
        />
      )}
      {showHistoryModal && patientCard && (
        <ConsultationHistoryModal
          patientCard={patientCard}
          patientName={profile?.name}
          onClose={() => setShowHistoryModal(false)}
        />
      )}
      {showReviewModal && patientCard && (
        <SessionReviewModal
          patientCard={patientCard}
          patientName={profile?.name}
          sessions={allSessions}
          treatmentStartDate={profile?.registrationDate || profile?.startDate}
          onClose={() => setShowReviewModal(false)}
        />
      )}
      {showReportsModal && patientCard && (
        <PatientReportsModal
          patientCard={patientCard}
          patientName={profile?.name}
          onClose={() => setShowReportsModal(false)}
        />
      )}
      {/* ── Remove Body Part Confirmation Modal ── */}
      {removeConfirm && (
        <div style={{ position:'fixed', inset:0, zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.5)' }}
          onClick={() => setRemoveConfirm(null)}>
          <div style={{ background:'#fff', borderRadius:16, padding:24, maxWidth:420, width:'90%', boxShadow:'0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 12px', fontSize:16, fontWeight:800, color:'#1e293b' }}>
              Remove "{removeConfirm.label}"?
            </h3>
            <p style={{ margin:'0 0 12px', fontSize:13, color:'#475569', lineHeight:1.5 }}>
              This will remove all symptoms for <strong>{removeConfirm.label}</strong> from this consultation.
            </p>
            {removeConfirm.exercises.length > 0 && (
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:12, marginBottom:12 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#dc2626', marginBottom:6 }}>
                  The following {removeConfirm.exercises.length} exercise{removeConfirm.exercises.length > 1 ? 's' : ''} will also be removed:
                </div>
                <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:'#7f1d1d', lineHeight:1.8 }}>
                  {removeConfirm.exercises.map((ex, i) => (
                    <li key={i}>{ex.name || ex.title || 'Unnamed exercise'}</li>
                  ))}
                </ul>
              </div>
            )}
            {removeConfirm.exercises.length === 0 && (
              <p style={{ margin:'0 0 12px', fontSize:12, color:'#6b7280', fontStyle:'italic' }}>
                No exercises are exclusively mapped to this body part.
              </p>
            )}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16 }}>
              <button
                onClick={() => setRemoveConfirm(null)}
                style={{ padding:'8px 20px', borderRadius:10, border:'1px solid #e2e8f0', background:'#f8fafc', color:'#475569', fontSize:13, fontWeight:600, cursor:'pointer' }}
              >Cancel</button>
              <button
                onClick={confirmRemovePart}
                style={{ padding:'8px 20px', borderRadius:10, border:'none', background:'#dc2626', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}
              >Remove</button>
            </div>
          </div>
        </div>
      )}

      {showPlanModal && patientCard && (
        <TreatmentPlanModal
          patientCard={patientCard}
          patientName={profile?.name}
          bodySymptoms={bodySymptoms}
          selectedSeqs={selectedSeqs}
          selectedRecomms={selectedRecomms}
          partComments={partComments}
          generalComments={generalComments}
          prevConsultParts={prevConsultParts}
          prevBodySymptoms={prevDbSymptoms}
          existingPlan={
            consultation?.planStatus || consultation?.mythsessions || consultation?.maintsessions ||
            (consultation?.planServices || consultation?.suggestedServices || []).length > 0
              ? { mythsessions: consultation.mythsessions || 0, maintsessions: consultation.maintsessions || 0, preventivesessions: consultation.preventivesessions || 0, drcomments: consultation.drcomments || '' }
              : null
          }
          existingPlanPartHolds={planPartHolds}
          existingPlanExcludedParts={planExcludedParts}
          existingPlanRomLinkedSelections={planRomLinkedSelections}
          existingSuggestedServices={consultation?.suggestedServices || []}
          gtpPlans={consultation?.gtpPlans || []}
          completedSessionsCount={allSessions.filter(s => s.status === 'Completed').length}
          onClose={() => setShowPlanModal(false)}
          onSubmit={handlePlanSubmit}
        />
      )}

      {/* ── MyNotes Modal (owner/solution only) ── */}
      {/* ── OLD GTP Update Modal ── */}
      {showOldGtpModal && patientCard && (() => {
        var filteredSvcs = oldGtpAllSvcs.filter(function(s) {
          if (!oldGtpSearch) return true;
          return (s.name || '').toLowerCase().includes(oldGtpSearch.toLowerCase());
        });
        var totalQty = oldGtpServices.reduce(function(s, x) { return s + (Number(x.qty) || 0); }, 0);
        var totalAmt = oldGtpServices.reduce(function(s, x) { return s + (Number(x.qty) || 0) * (Number(x.rate) || 0); }, 0);

        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.45)', zIndex: 9500,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }} onClick={() => setShowOldGtpModal(false)}>
            <div onClick={function(e) { e.stopPropagation(); }} style={{
              background: '#fff', borderRadius: 16, width: 520, maxWidth: '95vw', maxHeight: '85vh',
              overflow: 'auto', boxShadow: '0 12px 40px rgba(0,0,0,0.2)', padding: '20px 24px',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: '#92400e' }}>Update Old Sessions (Paper Records)</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{profile?.name || ''} — {patientCard}</div>
                </div>
                <button onClick={function() { setShowOldGtpModal(false); }} style={{
                  background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#64748b', fontWeight: 700
                }}>✕</button>
              </div>

              {/* Service picker */}
              <div style={{ marginBottom: 12 }}>
                <input
                  type="text" placeholder="Search services..." value={oldGtpSearch}
                  onChange={function(e) { setOldGtpSearch(e.target.value); }}
                  style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, boxSizing: 'border-box' }}
                />
                {oldGtpSearch && filteredSvcs.length > 0 && (
                  <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 160, overflow: 'auto', marginTop: 4, background: '#fff' }}>
                    {filteredSvcs.map(function(svc) {
                      var alreadyAdded = oldGtpServices.some(function(x) { return x.name === svc.name; });
                      return (
                        <div key={svc._id} onClick={function() {
                          if (!alreadyAdded) {
                            setOldGtpServices(function(prev) { return prev.concat([{
                              name: svc.name, qty: 1, rate: svc.amount || 0,
                              sgst: svc.sgst || 0, cgst: svc.cgst || 0, igst: svc.igst || 0, ecess: svc.ecess || 0,
                            }]); });
                            setOldGtpSearch('');
                          }
                        }} style={{
                          padding: '6px 12px', cursor: alreadyAdded ? 'default' : 'pointer', fontSize: 12,
                          background: alreadyAdded ? '#f1f5f9' : '#fff', color: alreadyAdded ? '#94a3b8' : '#1e293b',
                          borderBottom: '1px solid #f1f5f9',
                        }}>
                          {svc.name} {alreadyAdded ? '(added)' : ''} <span style={{ color: '#64748b', marginLeft: 8 }}>₹{svc.amount || 0}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Selected services table */}
              {oldGtpServices.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 60px 70px 80px 30px', gap: 4, fontSize: 10, fontWeight: 700, color: '#64748b', padding: '0 4px', marginBottom: 4 }}>
                    <span>Service</span><span>Qty</span><span>Rate</span><span>Amount</span><span></span>
                  </div>
                  {oldGtpServices.map(function(svc, idx) {
                    var amt = (Number(svc.qty) || 0) * (Number(svc.rate) || 0);
                    return (
                      <div key={idx} style={{
                        display: 'grid', gridTemplateColumns: '1fr 60px 70px 80px 30px', gap: 4, alignItems: 'center',
                        padding: '6px 4px', borderBottom: '1px solid #f1f5f9', fontSize: 12,
                      }}>
                        <span style={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{svc.name}</span>
                        <input type="number" min="1" value={svc.qty} onChange={function(e) {
                          setOldGtpServices(function(prev) { var n = prev.slice(); n[idx] = Object.assign({}, n[idx], { qty: Number(e.target.value) || 0 }); return n; });
                        }} style={{ width: '100%', padding: '4px 6px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12, textAlign: 'center' }} />
                        <input type="number" min="0" value={svc.rate} onChange={function(e) {
                          setOldGtpServices(function(prev) { var n = prev.slice(); n[idx] = Object.assign({}, n[idx], { rate: Number(e.target.value) || 0 }); return n; });
                        }} style={{ width: '100%', padding: '4px 6px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 12, textAlign: 'right' }} />
                        <span style={{ fontWeight: 700, color: '#16a34a', textAlign: 'right' }}>₹{amt.toLocaleString('en-IN')}</span>
                        <button onClick={function() {
                          setOldGtpServices(function(prev) { return prev.filter(function(_, i) { return i !== idx; }); });
                        }} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14, fontWeight: 700, padding: 0 }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Totals + paid input */}
              <div style={{ background: '#fffbeb', borderRadius: 12, padding: '12px 16px', marginBottom: 16, border: '1px solid #fbbf24' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>Total Sessions</span>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#1e293b' }}>{totalQty}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>Total Amount</span>
                  <span style={{ fontWeight: 800, fontSize: 15, color: '#16a34a' }}>₹{totalAmt.toLocaleString('en-IN')}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, borderTop: '1px dashed #fbbf24', paddingTop: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#92400e' }}>Already Paid</span>
                  <input type="number" min="0" value={oldGtpPaid} onChange={function(e) { setOldGtpPaid(Number(e.target.value) || 0); }}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13, textAlign: 'right' }} />
                </div>
                {totalAmt - oldGtpPaid > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: '#dc2626' }}>Pending Amount</span>
                    <span style={{ fontWeight: 800, fontSize: 14, color: '#dc2626' }}>₹{(totalAmt - oldGtpPaid).toLocaleString('en-IN')}</span>
                  </div>
                )}
              </div>

              {/* Toast notification */}
              {oldGtpToast && (
                <div style={{
                  padding: '12px 16px', borderRadius: 12, marginBottom: 12,
                  background: oldGtpToast.type === 'success' ? 'linear-gradient(135deg, #dcfce7, #bbf7d0)' : 'linear-gradient(135deg, #fee2e2, #fecaca)',
                  border: '1px solid ' + (oldGtpToast.type === 'success' ? '#86efac' : '#fca5a5'),
                  display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <span style={{ fontSize: 20 }}>{oldGtpToast.type === 'success' ? '✅' : '❌'}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: oldGtpToast.type === 'success' ? '#166534' : '#991b1b', flex: 1 }}>{oldGtpToast.msg}</span>
                  <button onClick={function() { setOldGtpToast(null); if (oldGtpToast.type === 'success') { setShowOldGtpModal(false); setOldGtpServices([]); setOldGtpPaid(0); setOldGtpConfirm(false); } }}
                    style={{ background: 'none', border: 'none', fontSize: 14, cursor: 'pointer', color: '#64748b', fontWeight: 700 }}>✕</button>
                </div>
              )}

              {/* Inline confirmation step */}
              {oldGtpConfirm && !oldGtpToast && (
                <div style={{
                  padding: '14px 16px', borderRadius: 12, marginBottom: 12,
                  background: 'linear-gradient(135deg, #fef3c7, #fde68a)',
                  border: '1.5px solid #f59e0b',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 10 }}>
                    This will permanently add <strong>{totalQty} old sessions</strong> (as completed, led by Dr Deepak) to <strong>{profile?.name || patientCard}</strong>. Continue?
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button disabled={oldGtpSaving} onClick={function() {
                      setOldGtpSaving(true);
                      setOldGtpToast(null);
                      api.post(process.env.REACT_APP_URL + '/patients/old-gtp-update/' + patientCard, {
                        services: oldGtpServices,
                        paidAmount: oldGtpPaid,
                        leadTherapist: 'drdeepak',
                      }).then(function(r) {
                        setOldGtpToast({ type: 'success', msg: 'Updated! ' + (r.data?.sessionsCreated || totalQty) + ' sessions added. Pending visits: ' + (r.data?.pendingvisits ?? '—') });
                        setOldGtpConfirm(false);
                      }).catch(function(err) {
                        setOldGtpToast({ type: 'error', msg: err.response?.data?.message || err.message || 'Something went wrong' });
                        setOldGtpConfirm(false);
                      }).finally(function() { setOldGtpSaving(false); });
                    }} style={{
                      flex: 1, padding: '10px', borderRadius: 8, border: 'none',
                      background: '#d97706', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer',
                    }}>
                      {oldGtpSaving ? 'Saving...' : 'Yes, Update'}
                    </button>
                    <button disabled={oldGtpSaving} onClick={function() { setOldGtpConfirm(false); }} style={{
                      padding: '10px 20px', borderRadius: 8, border: '1px solid #d1d5db',
                      background: '#fff', color: '#64748b', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Submit button */}
              {!oldGtpConfirm && !oldGtpToast && (
                <button
                  disabled={oldGtpSaving || oldGtpServices.length === 0}
                  onClick={function() { setOldGtpConfirm(true); }}
                  style={{
                    width: '100%', padding: '12px', borderRadius: 10, border: 'none',
                    background: oldGtpServices.length === 0 ? '#e2e8f0' : '#d97706', color: '#fff',
                    fontWeight: 800, fontSize: 14, cursor: oldGtpServices.length === 0 ? 'default' : 'pointer',
                  }}
                >
                  Update {totalQty} Old Sessions
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {showMyNotes && isOwnerOrSolution && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 9000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }} onClick={() => setShowMyNotes(false)}>
          <div style={{
            background: '#fff', borderRadius: 16, width: '90%', maxWidth: 560,
            maxHeight: '80vh', display: 'flex', flexDirection: 'column',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{
              padding: '16px 20px', borderBottom: '1px solid #e5e7eb',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#92400e' }}>📝 My Notes — {profile?.name || patientCard}</div>
              <button onClick={() => setShowMyNotes(false)} style={{
                background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', padding: '0 4px',
              }}>✕</button>
            </div>

            {/* Add note */}
            <div style={{ padding: '12px 20px', borderBottom: '1px solid #f3f4f6' }}>
              <textarea
                value={newNoteText}
                onChange={e => setNewNoteText(e.target.value)}
                placeholder="Add a private note..."
                rows={3}
                style={{
                  width: '100%', border: '1.5px solid #e5e7eb', borderRadius: 10,
                  padding: '10px 12px', fontSize: 13, resize: 'vertical',
                  fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                }}
              />
              <button
                onClick={addMyNote}
                disabled={!newNoteText.trim()}
                style={{
                  marginTop: 8, padding: '8px 20px', borderRadius: 8,
                  border: 'none', background: newNoteText.trim() ? '#f59e0b' : '#e5e7eb',
                  color: newNoteText.trim() ? '#fff' : '#9ca3af',
                  fontWeight: 600, fontSize: 13, cursor: newNoteText.trim() ? 'pointer' : 'default',
                }}
              >Add Note</button>
            </div>

            {/* Notes list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 20px 16px' }}>
              {myNotesLoading && <div style={{ textAlign: 'center', padding: 20, color: '#9ca3af' }}>Loading...</div>}
              {!myNotesLoading && myNotes.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: '#9ca3af', fontSize: 13 }}>No notes yet. Add your first private note above.</div>
              )}
              {!myNotesLoading && myNotes.slice().reverse().map(function(note) {
                var isEditing = editingNoteId === note._id;
                return (
                  <div key={note._id} style={{
                    padding: '12px 0', borderBottom: '1px solid #f9fafb',
                  }}>
                    {isEditing ? (
                      <div>
                        <textarea
                          value={editingNoteText}
                          onChange={e => setEditingNoteText(e.target.value)}
                          rows={3}
                          style={{
                            width: '100%', border: '1.5px solid #fbbf24', borderRadius: 8,
                            padding: '8px 10px', fontSize: 13, resize: 'vertical',
                            fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                          <button onClick={() => updateMyNote(note._id)} style={{
                            padding: '6px 14px', borderRadius: 6, border: 'none',
                            background: '#f59e0b', color: '#fff', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                          }}>Save</button>
                          <button onClick={() => { setEditingNoteId(null); setEditingNoteText(''); }} style={{
                            padding: '6px 14px', borderRadius: 6, border: '1px solid #d1d5db',
                            background: '#fff', color: '#6b7280', fontWeight: 600, fontSize: 12, cursor: 'pointer',
                          }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{ fontSize: 13, color: '#1f2937', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{note.text}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 6 }}>
                          <span style={{ fontSize: 10, color: '#9ca3af' }}>
                            {note.createdBy || ''} · {note.createdAt ? new Date(note.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            {note.updatedAt ? ' (edited)' : ''}
                          </span>
                          <button onClick={() => { setEditingNoteId(note._id); setEditingNoteText(note.text); }} style={{
                            background: 'none', border: 'none', color: '#3b82f6', fontSize: 11, cursor: 'pointer', fontWeight: 600, padding: 0,
                          }}>Edit</button>
                          <button onClick={() => deleteMyNote(note._id)} style={{
                            background: 'none', border: 'none', color: '#ef4444', fontSize: 11, cursor: 'pointer', fontWeight: 600, padding: 0,
                          }}>Delete</button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Small helper components ────────────────────────────── */
function VitalItem({ label, value }) {
  return (
    <div className="civ-vital-item">
      <span className="civ-vital-label">{label}:</span>
      <span className="civ-vital-value">{value}</span>
    </div>
  );
}

function PainDot({ part, symptoms, isSelected, readiness, hasPrevData, onClick, isExcluded, isPlanHeld }) {
  const [hovered, setHovered] = useState(false);
  const s = symptoms || {};
  const pain = s.pain || 0;
  const maxSym = Math.max(pain, s.numbness||0, s.weakness||0, s.stiffness||0,
                          s.burning||0, s.spasm||0, s.instability||0);
  const hasAnyData = maxSym > 0;
  const colorScore = pain > 0 ? pain : maxSym;
  const prevOnly   = hasPrevData && !hasAnyData;

  // Fill color: readiness overrides default when no pain data
  const fill   = isExcluded  ? "rgba(180,180,190,0.18)"
               : isPlanHeld  ? "rgba(251,191,36,0.28)"
               : hasAnyData  ? getPainColor(colorScore)
               : readiness === 'ready'      ? "rgba(59,130,246,0.45)"
               : readiness === 'incomplete' ? "rgba(236,72,153,0.40)"
               : prevOnly    ? "rgba(255,130,200,0.12)"
               :               "rgba(255,130,200,0.08)";
  const stroke = isExcluded  ? "rgba(130,130,145,0.40)"
               : isPlanHeld  ? "rgba(217,119,6,0.85)"
               : hasAnyData  ? getPainStroke(colorScore)
               : readiness === 'ready'      ? "rgba(59,130,246,0.90)"
               : readiness === 'incomplete' ? "rgba(236,72,153,0.85)"
               : prevOnly    ? "rgba(255,80,180,0.85)"
               :               "rgba(255,80,180,0.90)";
  const glow   = isExcluded || isPlanHeld ? "none"
               : hasAnyData ? getPainGlow(colorScore)
               : "drop-shadow(0 0 3px rgba(255,80,180,0.50))";
  // show label only on hover
  const showLabel = hovered;

  return (
    <g style={{ cursor:"pointer" }} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => e.key === "Enter" && onClick()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <title>{part.label}{isExcluded ? ' — excluded from plan' : isPlanHeld ? ' — on hold' : pain > 0 ? ` — Pain: ${pain}/10` : hasAnyData ? ` — active` : hasPrevData ? ' (prev session)' : ''}</title>
      {/* Readiness ring: blue = ready, orange = incomplete; dashed ring for focused/selected */}
      {readiness === 'ready' && !isSelected && (
        <circle cx={part.cx} cy={part.cy} r={10}
          fill="none" stroke="rgba(59,130,246,0.80)" strokeWidth="2" />
      )}
      {readiness === 'incomplete' && !isSelected && (
        <circle cx={part.cx} cy={part.cy} r={10}
          fill="none" stroke="rgba(236,72,153,0.80)" strokeWidth="2" />
      )}
      {isSelected && (
        <circle cx={part.cx} cy={part.cy} r={10}
          fill="none" stroke="rgba(45,95,160,0.90)" strokeWidth="2"
          strokeDasharray="4 2" />
      )}
      {/* Amber dashed ring for plan-held parts */}
      {isPlanHeld && !isExcluded && (
        <circle cx={part.cx} cy={part.cy} r={9}
          fill="none" stroke="rgba(217,119,6,0.90)" strokeWidth="2"
          strokeDasharray="3 2" />
      )}
      {/* Subtle always-visible indicator ring for inactive/selectable parts */}
      {!isExcluded && !isPlanHeld && !hasAnyData && !prevOnly && (
        <circle cx={part.cx} cy={part.cy} r={8}
          fill="none" stroke="rgba(255,80,180,0.45)" strokeWidth="1.2"
          strokeDasharray="3 3" />
      )}
      {/* Main dot */}
      <circle cx={part.cx} cy={part.cy} r={7}
        fill={fill} stroke={stroke} strokeWidth={isExcluded ? "1" : "1.5"}
        style={{ filter: glow, opacity: isExcluded ? 0.55 : 1 }}
      />
      {/* Excluded: ✕ overlay */}
      {isExcluded && (
        <text x={part.cx} y={part.cy + 4} textAnchor="middle"
          fontSize="9" fontWeight="700" fill="rgba(110,115,130,0.80)"
          style={{ pointerEvents:"none" }}>✕</text>
      )}
      {/* Held: lock text overlay */}
      {isPlanHeld && !isExcluded && (
        <text x={part.cx} y={part.cy + 4} textAnchor="middle"
          fontSize="9" fill="rgba(180,90,0,0.90)"
          style={{ pointerEvents:"none" }}>🔒</text>
      )}
      {/* Pain score inside dot — only for normal parts */}
      {!isExcluded && !isPlanHeld && pain > 0 && (
        <text x={part.cx} y={part.cy + 4} textAnchor="middle"
          fontSize="9" fontWeight="700" fill="rgba(255,255,255,0.95)"
          style={{ pointerEvents:"none" }}>{pain}</text>
      )}
      {/* Non-pain symptom indicator */}
      {!isExcluded && !isPlanHeld && pain === 0 && maxSym > 0 && (
        <text x={part.cx} y={part.cy + 4} textAnchor="middle"
          fontSize="8" fontWeight="700" fill="rgba(255,255,255,0.85)"
          style={{ pointerEvents:"none" }}>●</text>
      )}
      {/* Label below dot — hover only */}
      {showLabel && (
        <>
          <rect
            x={part.cx - 24} y={part.cy + 8}
            width={48} height={12} rx={2}
            fill={hasAnyData ? "rgba(20,20,40,0.75)" : "rgba(60,20,140,0.80)"}
            style={{ pointerEvents:"none" }}
          />
          <text x={part.cx} y={part.cy + 17} textAnchor="middle"
            fontSize="9.5" fontWeight="700"
            fill={isExcluded ? "rgba(180,185,210,0.70)"
                : isPlanHeld ? "rgba(255,190,50,0.95)"
                :              "rgba(255,255,255,0.98)"}
            style={{ pointerEvents:"none" }}>
            {part.label}
          </text>
        </>
      )}
    </g>
  );
}

/* ── PainLine: line-type body-part annotation (from BPConfig) ─────────────── */
function PainLine({ part, symptoms, isSelected, readiness, hasPrevData, onClick, isExcluded, isPlanHeld }) {
  const [hovered, setHovered] = useState(false);
  const s = symptoms || {};
  const pain     = s.pain || 0;
  const maxSym   = Math.max(pain, s.numbness||0, s.weakness||0, s.stiffness||0,
                            s.burning||0, s.spasm||0, s.instability||0);
  const hasAnyData = maxSym > 0;
  const colorScore = pain > 0 ? pain : maxSym;
  const prevOnly   = hasPrevData && !hasAnyData;

  const lineColor = isExcluded  ? "rgba(160,160,170,0.55)"
                  : isPlanHeld  ? "rgba(217,119,6,0.85)"
                  : hasAnyData  ? getPainStroke(colorScore)
                  : readiness === 'ready'      ? "rgba(59,130,246,0.90)"
                  : readiness === 'incomplete' ? "rgba(236,72,153,0.85)"
                  : prevOnly    ? "rgba(160,0,255,0.90)"
                  :               "rgba(160,0,255,0.95)";
  const lineFilter = isExcluded || isPlanHeld ? "none"
                   : hasAnyData ? getPainGlow(colorScore)
                   : "drop-shadow(0 0 4px rgba(160,0,255,0.70))";

  const isTriple = part.lineStyle === 'triple';
  const midX = Math.round((part.x1 + part.x2) / 2);
  const midY = Math.round((part.y1 + part.y2) / 2);
  const { px, py } = isTriple ? perpVec(part.x1, part.y1, part.x2, part.y2, 4) : { px: 0, py: 0 };
  const shortLabel = part.label.length > 9 ? part.label.slice(0, 8) + "…" : part.label;
  const sw = hasAnyData ? 3.0 : isSelected ? 2.5 : 2.0;

  return (
    <g style={{ cursor: 'pointer' }} onClick={onClick} role="button" tabIndex={0}
      onKeyDown={e => e.key === 'Enter' && onClick()}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <title>{part.label}{isExcluded ? ' — excluded from plan' : isPlanHeld ? ' — on hold' : pain > 0 ? ` — Pain: ${pain}/10` : hasAnyData ? ' — active' : hasPrevData ? ' (prev session)' : ''}</title>

      {/* Fat invisible hit area */}
      <line x1={part.x1} y1={part.y1} x2={part.x2} y2={part.y2} stroke="transparent" strokeWidth={18} />

      {/* Readiness ring: blue = ready, orange = incomplete */}
      {readiness === 'ready' && !isSelected && (
        <circle cx={midX} cy={midY} r={11}
          fill="none" stroke="rgba(59,130,246,0.80)" strokeWidth="2" />
      )}
      {readiness === 'incomplete' && !isSelected && (
        <circle cx={midX} cy={midY} r={11}
          fill="none" stroke="rgba(236,72,153,0.80)" strokeWidth="2" />
      )}
      {isSelected && (
        <circle cx={midX} cy={midY} r={11}
          fill="none" stroke="rgba(45,95,160,0.90)" strokeWidth="2"
          strokeDasharray="4 2" />
      )}
      {isPlanHeld && !isExcluded && (
        <circle cx={midX} cy={midY} r={10}
          fill="none" stroke="rgba(217,119,6,0.90)" strokeWidth="2" strokeDasharray="3 2" />
      )}
      {!isExcluded && !isPlanHeld && !hasAnyData && !prevOnly && (
        <circle cx={midX} cy={midY} r={8}
          fill="none" stroke="rgba(160,0,255,0.50)" strokeWidth="1.2" strokeDasharray="3 3" />
      )}

      {/* Main line(s) */}
      <line x1={part.x1} y1={part.y1} x2={part.x2} y2={part.y2}
        stroke={lineColor} strokeWidth={sw} strokeLinecap="round"
        style={{ filter: lineFilter, opacity: isExcluded ? 0.55 : 1 }} />
      {isTriple && (<>
        <line x1={part.x1+px} y1={part.y1+py} x2={part.x2+px} y2={part.y2+py}
          stroke={lineColor} strokeWidth={sw - 0.8} strokeLinecap="round"
          style={{ opacity: isExcluded ? 0.55 : 0.85 }} />
        <line x1={part.x1-px} y1={part.y1-py} x2={part.x2-px} y2={part.y2-py}
          stroke={lineColor} strokeWidth={sw - 0.8} strokeLinecap="round"
          style={{ opacity: isExcluded ? 0.55 : 0.85 }} />
      </>)}

      {/* Overlay: excluded ✕ / held 🔒 / pain score / symptom dot */}
      {isExcluded && (
        <text x={midX} y={midY+4} textAnchor="middle" fontSize="9" fontWeight="700"
          fill="rgba(110,115,130,0.80)" style={{ pointerEvents:'none' }}>✕</text>
      )}
      {isPlanHeld && !isExcluded && (
        <text x={midX} y={midY+4} textAnchor="middle" fontSize="9"
          fill="rgba(180,90,0,0.90)" style={{ pointerEvents:'none' }}>🔒</text>
      )}
      {!isExcluded && !isPlanHeld && pain > 0 && (
        <text x={midX} y={midY+4} textAnchor="middle" fontSize="9" fontWeight="700"
          fill="rgba(255,255,255,0.95)" style={{ pointerEvents:'none' }}>{pain}</text>
      )}
      {!isExcluded && !isPlanHeld && pain === 0 && maxSym > 0 && (
        <text x={midX} y={midY+4} textAnchor="middle" fontSize="8" fontWeight="700"
          fill="rgba(255,255,255,0.85)" style={{ pointerEvents:'none' }}>●</text>
      )}

      {/* Label — hover only */}
      {hovered && (
        <>
          <rect x={midX-24} y={midY+8} width={48} height={12} rx={2}
            fill={hasAnyData ? "rgba(20,20,40,0.75)" : "rgba(60,20,140,0.80)"}
            style={{ pointerEvents:'none' }} />
          <text x={midX} y={midY+17} textAnchor="middle"
            fontSize="9.5" fontWeight="700"
            fill={isExcluded ? "rgba(180,185,210,0.70)"
                : isPlanHeld ? "rgba(255,190,50,0.95)"
                :              "rgba(255,255,255,0.98)"}
            style={{ pointerEvents:'none' }}>
            {part.label}
          </text>
        </>
      )}
    </g>
  );
}

/* ── BACKUP: Human body SVG figures (preserved — not deleted) ────────────────
   These are no longer rendered directly; the body map now uses front_body.png /
   back_body.png instead. Keep here so we can revert without losing the SVG work.
   Revert guide: see src/components/CIVBodyBackup.js
   ──────────────────────────────────────────────────────────────────────────── */
function FrontBodySVG() {
  const SKIN = "rgba(230,185,148,0.12)";
  const SSTR = "rgba(205,162,118,0.38)";
  const BONE = "rgba(238,222,182,0.90)";
  const BSTR = "rgba(205,188,140,0.88)";
  const MUS  = "rgba(186,52,32,0.68)";
  const MSTR = "rgba(145,28,16,0.60)";
  const TEND = "rgba(218,178,60,0.82)";
  const CART = "rgba(110,188,225,0.70)";
  const NERV = "rgba(252,214,28,0.80)";
  return (
    <g>
      <defs>
        <radialGradient id="bgGF" cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#18263a"/>
          <stop offset="100%" stopColor="#0a1018"/>
        </radialGradient>
        <clipPath id="tcF"><rect x="147" y="88" width="98" height="145"/></clipPath>
        <filter id="ngF" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="1.8" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <rect x="0" y="0" width="390" height="660" fill="url(#bgGF)" />
      {/* ── Skin silhouettes ── */}
      {/* Head */}
      <ellipse cx="195" cy="34" rx="26" ry="30" fill={SKIN} stroke={SSTR} strokeWidth="1.2"/>
      {/* Neck */}
      <rect x="185" y="62" width="20" height="24" rx="4" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Torso */}
      <path d="M152,84 C145,100 140,162 142,200 C144,222 160,242 195,244 C230,242 246,222 248,200 C250,162 245,100 238,84 C228,78 210,74 195,74 C180,74 162,78 152,84Z" fill={SKIN} stroke={SSTR} strokeWidth="1"/>
      {/* Left upper arm */}
      <path d="M152,90 C138,108 122,148 112,192 C108,210 110,240 120,240 C130,240 132,210 134,192 L148,144Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left forearm */}
      <path d="M108,240 C102,256 98,272 98,288 L108,292 L122,244Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left hand */}
      <path d="M91,268 C82,276 74,290 70,308 C67,322 68,336 71,342 C72,348 72,356 73,360 L77,360 C78,350 78,340 78,334 C79,340 80,354 82,360 L86,360 C86,350 86,340 86,334 C87,340 89,354 92,360 L96,360 C96,350 96,340 97,334 C98,340 101,352 104,358 L107,356 C108,348 108,336 110,328 C114,316 115,300 110,288 C106,276 100,268 91,268Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right upper arm */}
      <path d="M238,90 C252,108 268,148 278,192 C282,210 280,240 270,240 C260,240 258,210 256,192 L242,144Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right forearm */}
      <path d="M282,240 C288,256 292,272 292,288 L282,292 L268,244Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right hand */}
      <path d="M299,268 C308,276 316,290 320,308 C323,322 322,336 319,342 C318,348 318,356 317,360 L313,360 C312,350 312,340 312,334 C311,340 310,354 308,360 L304,360 C304,350 304,340 303,334 C302,340 300,354 298,360 L294,360 C294,350 294,340 293,334 C292,340 289,352 286,358 L283,356 C282,348 282,336 280,328 C276,316 275,300 280,288 C284,276 290,268 299,268Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Pelvis region */}
      <path d="M155,202 C150,224 150,250 158,270 L192,272 L198,272 L232,270 C240,250 240,224 235,202Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left thigh */}
      <path d="M158,272 C152,294 150,330 152,360 C154,376 162,384 174,380 L180,320 L192,272Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left lower leg */}
      <path d="M152,364 C148,390 148,426 150,462 C152,476 162,482 174,478 L178,440 L178,368Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left foot */}
      <path d="M148,470 C144,488 140,508 138,528 C136,548 137,562 140,572 C143,582 150,590 158,594 C166,598 176,596 183,590 L186,574 C182,582 170,585 160,580 C152,574 148,562 148,548 C148,532 152,514 155,498 C157,485 156,475 152,472Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left toes */}
      <ellipse cx="145" cy="595" rx="5" ry="9" fill={SKIN} stroke={SSTR} strokeWidth="0.7" transform="rotate(-10,145,595)"/>
      <ellipse cx="155" cy="598" rx="4" ry="8" fill={SKIN} stroke={SSTR} strokeWidth="0.7" transform="rotate(-5,155,598)"/>
      <ellipse cx="163" cy="598" rx="4" ry="8" fill={SKIN} stroke={SSTR} strokeWidth="0.7"/>
      <ellipse cx="171" cy="596" rx="4" ry="7" fill={SKIN} stroke={SSTR} strokeWidth="0.7"/>
      <ellipse cx="178" cy="592" rx="3" ry="6" fill={SKIN} stroke={SSTR} strokeWidth="0.7"/>
      {/* Right thigh */}
      <path d="M232,272 C238,294 240,330 238,360 C236,376 228,384 216,380 L210,320 L198,272Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right lower leg */}
      <path d="M238,364 C242,390 242,426 240,462 C238,476 228,482 216,478 L212,440 L212,368Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right foot */}
      <path d="M242,470 C246,488 250,508 252,528 C254,548 253,562 250,572 C247,582 240,590 232,594 C224,598 214,596 207,590 L204,574 C208,582 220,585 230,580 C238,574 242,562 242,548 C242,532 238,514 235,498 C233,485 234,475 238,472Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right toes */}
      <ellipse cx="245" cy="595" rx="5" ry="9" fill={SKIN} stroke={SSTR} strokeWidth="0.7" transform="rotate(10,245,595)"/>
      <ellipse cx="235" cy="598" rx="4" ry="8" fill={SKIN} stroke={SSTR} strokeWidth="0.7" transform="rotate(5,235,598)"/>
      <ellipse cx="227" cy="598" rx="4" ry="8" fill={SKIN} stroke={SSTR} strokeWidth="0.7"/>
      <ellipse cx="219" cy="596" rx="4" ry="7" fill={SKIN} stroke={SSTR} strokeWidth="0.7"/>
      <ellipse cx="212" cy="592" rx="3" ry="6" fill={SKIN} stroke={SSTR} strokeWidth="0.7"/>

      {/* ── Skeleton ── */}
      {/* Skull */}
      <ellipse cx="195" cy="30" rx="22" ry="25" fill="none" stroke={BONE} strokeWidth="1.2"/>
      <path d="M176,42 Q195,58 214,42" fill="none" stroke={BONE} strokeWidth="1.2"/>
      <ellipse cx="186" cy="30" rx="5" ry="4" fill="none" stroke={BONE} strokeWidth="0.9"/>
      <ellipse cx="204" cy="30" rx="5" ry="4" fill="none" stroke={BONE} strokeWidth="0.9"/>
      {/* Cervical vertebrae */}
      <rect x="191" y="64" width="8" height="5" rx="1" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      <rect x="191" y="70" width="8" height="5" rx="1" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      <rect x="191" y="76" width="8" height="5" rx="1" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      {/* Clavicles */}
      <path d="M191,84 C182,86 170,88 158,92" stroke={BONE} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <path d="M199,84 C208,86 220,88 232,92" stroke={BONE} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      {/* Sternum */}
      <rect x="192" y="90" width="6" height="55" rx="2" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      <path d="M192,145 L195,155 L198,145" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
      {/* Ribs L */}
      <path d="M192,95  C180,96  166,100 160,112" fill="none" stroke={BONE} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M192,105 C180,108 165,114 160,126" fill="none" stroke={BONE} strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M192,115 C180,118 165,124 162,136" fill="none" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M192,125 C180,128 166,132 164,142" fill="none" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M160,112 C156,118 155,126 160,126" fill="none" stroke={CART} strokeWidth="1.2"/>
      <path d="M160,126 C155,132 154,140 162,136" fill="none" stroke={CART} strokeWidth="1.1"/>
      {/* Ribs R */}
      <path d="M198,95  C210,96  224,100 230,112" fill="none" stroke={BONE} strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M198,105 C210,108 225,114 230,126" fill="none" stroke={BONE} strokeWidth="1.4" strokeLinecap="round"/>
      <path d="M198,115 C210,118 225,124 228,136" fill="none" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <path d="M198,125 C210,128 224,132 226,142" fill="none" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      <path d="M230,112 C234,118 235,126 230,126" fill="none" stroke={CART} strokeWidth="1.2"/>
      <path d="M230,126 C235,132 236,140 228,136" fill="none" stroke={CART} strokeWidth="1.1"/>
      {/* Lumbar vertebrae */}
      <rect x="190" y="155" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      <rect x="190" y="165" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      <rect x="190" y="175" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      <rect x="190" y="185" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      <rect x="190" y="195" width="10" height="8" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
      {/* Pelvis */}
      <path d="M158,204 C152,214 152,230 160,242 C168,252 180,258 195,258 C210,258 222,252 230,242 C238,230 238,214 232,204 C224,198 210,194 195,194 C180,194 166,198 158,204Z" fill="none" stroke={BONE} strokeWidth="1.5"/>
      <circle cx="163" cy="244" r="9" fill="none" stroke={BONE} strokeWidth="1.2"/>
      <circle cx="227" cy="244" r="9" fill="none" stroke={BONE} strokeWidth="1.2"/>
      {/* Femurs */}
      <line x1="168" y1="252" x2="160" y2="386" stroke={BONE} strokeWidth="2.8" strokeLinecap="round"/>
      <line x1="222" y1="252" x2="230" y2="386" stroke={BONE} strokeWidth="2.8" strokeLinecap="round"/>
      {/* Patellae */}
      <ellipse cx="160" cy="389" rx="11" ry="9" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      <ellipse cx="230" cy="389" rx="11" ry="9" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      {/* Tibia L / Fibula L */}
      <line x1="158" y1="401" x2="155" y2="500" stroke={BONE} strokeWidth="2.4" strokeLinecap="round"/>
      <line x1="167" y1="403" x2="164" y2="494" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      {/* Tibia R / Fibula R */}
      <line x1="232" y1="401" x2="235" y2="500" stroke={BONE} strokeWidth="2.4" strokeLinecap="round"/>
      <line x1="223" y1="403" x2="226" y2="494" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      {/* Talus / ankle L */}
      <ellipse cx="156" cy="504" rx="10" ry="7" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      {/* Talus / ankle R */}
      <ellipse cx="234" cy="504" rx="10" ry="7" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      {/* Metatarsals L */}
      <line x1="140" y1="514" x2="140" y2="556" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="146" y1="515" x2="146" y2="558" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="152" y1="515" x2="152" y2="559" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="158" y1="514" x2="158" y2="557" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="163" y1="512" x2="163" y2="552" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Metatarsals R */}
      <line x1="250" y1="514" x2="250" y2="556" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="244" y1="515" x2="244" y2="558" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="238" y1="515" x2="238" y2="559" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="232" y1="514" x2="232" y2="557" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      <line x1="227" y1="512" x2="227" y2="552" stroke={BONE} strokeWidth="1.2" strokeLinecap="round"/>
      {/* Toe phalanges L */}
      {[140,146,152,158,163].map((x,i) => (<g key={i}><line x1={x} y1={556+i*(-1)} x2={x} y2={576+i*(-1)} stroke={BONE} strokeWidth="1.1"/><line x1={x} y1={576+i*(-1)} x2={x} y2={592+i*(-2)} stroke={BONE} strokeWidth="0.9"/></g>))}
      {/* Toe phalanges R */}
      {[250,244,238,232,227].map((x,i) => (<g key={i}><line x1={x} y1={556+i*(-1)} x2={x} y2={576+i*(-1)} stroke={BONE} strokeWidth="1.1"/><line x1={x} y1={576+i*(-1)} x2={x} y2={592+i*(-2)} stroke={BONE} strokeWidth="0.9"/></g>))}
      {/* Humerus L */}
      <line x1="150" y1="98" x2="108" y2="210" stroke={BONE} strokeWidth="2.4" strokeLinecap="round"/>
      {/* Radius / Ulna L */}
      <line x1="104" y1="216" x2="92" y2="268" stroke={BONE} strokeWidth="2.0" strokeLinecap="round"/>
      <line x1="112" y1="216" x2="102" y2="266" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      {/* Wrist carpals L */}
      <rect x="85" y="268" width="20" height="10" rx="2.5" fill={BONE} opacity="0.7"/>
      {/* Metacarpals L */}
      <line x1="106" y1="278" x2="108" y2="318" stroke={BONE} strokeWidth="1.4"/>
      <line x1="100" y1="278" x2="100" y2="322" stroke={BONE} strokeWidth="1.4"/>
      <line x1="93"  y1="278" x2="90"  y2="322" stroke={BONE} strokeWidth="1.4"/>
      <line x1="86"  y1="278" x2="82"  y2="318" stroke={BONE} strokeWidth="1.4"/>
      <path d="M107,278 C110,290 113,306 114,318" stroke={BONE} strokeWidth="1.4" fill="none"/>
      {/* Finger phalanges L (index, middle, ring, little) */}
      <line x1="108" y1="318" x2="107" y2="336" stroke={BONE} strokeWidth="1.1"/>
      <line x1="107" y1="336" x2="105" y2="352" stroke={BONE} strokeWidth="0.9"/>
      <line x1="100" y1="322" x2="99"  y2="340" stroke={BONE} strokeWidth="1.1"/>
      <line x1="99"  y1="340" x2="97"  y2="358" stroke={BONE} strokeWidth="0.9"/>
      <line x1="90"  y1="322" x2="88"  y2="340" stroke={BONE} strokeWidth="1.1"/>
      <line x1="88"  y1="340" x2="86"  y2="356" stroke={BONE} strokeWidth="0.9"/>
      <line x1="82"  y1="318" x2="80"  y2="334" stroke={BONE} strokeWidth="1.1"/>
      <line x1="80"  y1="334" x2="78"  y2="348" stroke={BONE} strokeWidth="0.9"/>
      {/* Thumb L */}
      <line x1="114" y1="318" x2="116" y2="336" stroke={BONE} strokeWidth="1.1"/>
      <line x1="116" y1="336" x2="115" y2="350" stroke={BONE} strokeWidth="0.9"/>
      {/* Finger knuckle joints L */}
      <circle cx="108" cy="318" r="2.5" fill={CART} opacity="0.6"/>
      <circle cx="100" cy="322" r="2.5" fill={CART} opacity="0.6"/>
      <circle cx="90"  cy="322" r="2.5" fill={CART} opacity="0.6"/>
      <circle cx="82"  cy="318" r="2.5" fill={CART} opacity="0.6"/>
      <circle cx="114" cy="318" r="2.5" fill={CART} opacity="0.6"/>
      {/* Humerus R */}
      <line x1="240" y1="98" x2="282" y2="210" stroke={BONE} strokeWidth="2.4" strokeLinecap="round"/>
      {/* Radius / Ulna R */}
      <line x1="286" y1="216" x2="298" y2="268" stroke={BONE} strokeWidth="2.0" strokeLinecap="round"/>
      <line x1="278" y1="216" x2="288" y2="266" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      {/* Wrist carpals R */}
      <rect x="285" y="268" width="20" height="10" rx="2.5" fill={BONE} opacity="0.7"/>
      {/* Metacarpals R */}
      <line x1="284" y1="278" x2="282" y2="318" stroke={BONE} strokeWidth="1.4"/>
      <line x1="290" y1="278" x2="290" y2="322" stroke={BONE} strokeWidth="1.4"/>
      <line x1="297" y1="278" x2="300" y2="322" stroke={BONE} strokeWidth="1.4"/>
      <line x1="304" y1="278" x2="308" y2="318" stroke={BONE} strokeWidth="1.4"/>
      <path d="M283,278 C280,290 277,306 276,318" stroke={BONE} strokeWidth="1.4" fill="none"/>
      {/* Finger phalanges R */}
      <line x1="282" y1="318" x2="283" y2="336" stroke={BONE} strokeWidth="1.1"/>
      <line x1="283" y1="336" x2="285" y2="352" stroke={BONE} strokeWidth="0.9"/>
      <line x1="290" y1="322" x2="291" y2="340" stroke={BONE} strokeWidth="1.1"/>
      <line x1="291" y1="340" x2="293" y2="358" stroke={BONE} strokeWidth="0.9"/>
      <line x1="300" y1="322" x2="302" y2="340" stroke={BONE} strokeWidth="1.1"/>
      <line x1="302" y1="340" x2="304" y2="356" stroke={BONE} strokeWidth="0.9"/>
      <line x1="308" y1="318" x2="310" y2="334" stroke={BONE} strokeWidth="1.1"/>
      <line x1="310" y1="334" x2="312" y2="348" stroke={BONE} strokeWidth="0.9"/>
      {/* Thumb R */}
      <line x1="276" y1="318" x2="274" y2="336" stroke={BONE} strokeWidth="1.1"/>
      <line x1="274" y1="336" x2="275" y2="350" stroke={BONE} strokeWidth="0.9"/>
      {/* Finger knuckle joints R */}
      <circle cx="282" cy="318" r="2.5" fill={CART} opacity="0.6"/>
      <circle cx="290" cy="322" r="2.5" fill={CART} opacity="0.6"/>
      <circle cx="300" cy="322" r="2.5" fill={CART} opacity="0.6"/>
      <circle cx="308" cy="318" r="2.5" fill={CART} opacity="0.6"/>
      <circle cx="276" cy="318" r="2.5" fill={CART} opacity="0.6"/>

      {/* ── Joints / Cartilage ── */}
      <circle cx="150" cy="94"  r="9" fill={CART} opacity="0.5"/>
      <circle cx="240" cy="94"  r="9" fill={CART} opacity="0.5"/>
      <ellipse cx="108" cy="213" rx="9" ry="7" fill={CART} opacity="0.5"/>
      <ellipse cx="282" cy="213" rx="9" ry="7" fill={CART} opacity="0.5"/>
      <ellipse cx="160" cy="395" rx="11" ry="7" fill={CART} opacity="0.4"/>
      <ellipse cx="230" cy="395" rx="11" ry="7" fill={CART} opacity="0.4"/>
      {/* Patellar tendons */}
      <line x1="160" y1="399" x2="159" y2="412" stroke={TEND} strokeWidth="2.5" strokeLinecap="round"/>
      <line x1="230" y1="399" x2="231" y2="412" stroke={TEND} strokeWidth="2.5" strokeLinecap="round"/>
      {/* Wrist→palm radiocarpal joint twist L (shows hand starts here) */}
      <ellipse cx="89" cy="286" rx="11" ry="4.5" fill={CART} opacity="0.62"/>
      <line x1="79" y1="282" x2="99" y2="290" stroke={BONE} strokeWidth="1.2" opacity="0.55"/>
      <line x1="79" y1="290" x2="99" y2="282" stroke={BONE} strokeWidth="1.2" opacity="0.55"/>
      {/* Wrist→palm radiocarpal joint twist R */}
      <ellipse cx="301" cy="286" rx="11" ry="4.5" fill={CART} opacity="0.62"/>
      <line x1="291" y1="282" x2="311" y2="290" stroke={BONE} strokeWidth="1.2" opacity="0.55"/>
      <line x1="291" y1="290" x2="311" y2="282" stroke={BONE} strokeWidth="1.2" opacity="0.55"/>
      {/* Ankle cartilage */}
      <ellipse cx="155" cy="508" rx="10" ry="6" fill={CART} opacity="0.45"/>
      <ellipse cx="235" cy="508" rx="10" ry="6" fill={CART} opacity="0.45"/>
      {/* Ankle→foot talocrural joint twist L (foot starts here) */}
      <ellipse cx="152" cy="530" rx="14" ry="5" fill={CART} opacity="0.58"/>
      <line x1="138" y1="526" x2="166" y2="534" stroke={BONE} strokeWidth="1.2" opacity="0.55"/>
      <line x1="138" y1="534" x2="166" y2="526" stroke={BONE} strokeWidth="1.2" opacity="0.55"/>
      {/* Ankle→foot talocrural joint twist R */}
      <ellipse cx="238" cy="530" rx="14" ry="5" fill={CART} opacity="0.58"/>
      <line x1="224" y1="526" x2="252" y2="534" stroke={BONE} strokeWidth="1.2" opacity="0.55"/>
      <line x1="224" y1="534" x2="252" y2="526" stroke={BONE} strokeWidth="1.2" opacity="0.55"/>
      {/* Toe MTP joints L */}
      {[140,146,152,158,163].map((x,i) => <circle key={i} cx={x} cy={558-i} r="2" fill={CART} opacity="0.5"/>)}
      {/* Toe MTP joints R */}
      {[250,244,238,232,227].map((x,i) => <circle key={i} cx={x} cy={558-i} r="2" fill={CART} opacity="0.5"/>)}

      {/* ── Muscles ── */}
      <line x1="189" y1="66" x2="182" y2="88" stroke={MUS} strokeWidth="1.5"/>
      <line x1="201" y1="66" x2="208" y2="88" stroke={MUS} strokeWidth="1.5"/>
      {/* Pec L */}
      <path d="M186,92 C175,96 163,102 160,114 C158,126 166,134 180,134 C188,134 192,128 195,124" fill={MUS} opacity="0.7" stroke={MSTR} strokeWidth="0.8"/>
      <line x1="186" y1="94"  x2="162" y2="112" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      <line x1="186" y1="100" x2="162" y2="118" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      <line x1="186" y1="108" x2="162" y2="126" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      {/* Pec R */}
      <path d="M204,92 C215,96 227,102 230,114 C232,126 224,134 210,134 C202,134 198,128 195,124" fill={MUS} opacity="0.7" stroke={MSTR} strokeWidth="0.8"/>
      <line x1="204" y1="94"  x2="228" y2="112" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      <line x1="204" y1="100" x2="228" y2="118" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      <line x1="204" y1="108" x2="228" y2="126" stroke={MSTR} strokeWidth="0.7" opacity="0.6"/>
      {/* Deltoid L */}
      <path d="M148,88 C140,96 134,112 136,126 C140,130 148,126 152,116 L154,98Z" fill={MUS} opacity="0.65" stroke={MSTR} strokeWidth="0.8"/>
      {/* Deltoid R */}
      <path d="M242,88 C250,96 256,112 254,126 C250,130 242,126 238,116 L236,98Z" fill={MUS} opacity="0.65" stroke={MSTR} strokeWidth="0.8"/>
      {/* Biceps L */}
      <path d="M140,120 C134,136 130,158 132,174 C136,180 142,178 146,170 C144,154 142,138 142,122Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      <line x1="137" y1="175" x2="110" y2="210" stroke={TEND} strokeWidth="1.5"/>
      {/* Biceps R */}
      <path d="M250,120 C256,136 260,158 258,174 C254,180 248,178 244,170 C246,154 248,138 248,122Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      <line x1="253" y1="175" x2="280" y2="210" stroke={TEND} strokeWidth="1.5"/>
      {/* Abs */}
      <rect x="183" y="142" width="11" height="10" rx="2.5" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="196" y="142" width="11" height="10" rx="2.5" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="183" y="154" width="11" height="10" rx="2.5" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="196" y="154" width="11" height="10" rx="2.5" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="184" y="166" width="10" height="9"  rx="2.5" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="196" y="166" width="10" height="9"  rx="2.5" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      {/* Obliques */}
      <path d="M158,142 C154,162 152,182 154,202" stroke={MUS} strokeWidth="2.5" fill="none" opacity="0.6"/>
      <path d="M232,142 C236,162 238,182 236,202" stroke={MUS} strokeWidth="2.5" fill="none" opacity="0.6"/>
      {/* Quads L */}
      <path d="M160,274 C154,296 152,328 154,360" stroke={MUS} strokeWidth="9" fill="none" opacity="0.48" strokeLinecap="round"/>
      <line x1="157" y1="285" x2="155" y2="318" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      <line x1="163" y1="280" x2="161" y2="312" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      {/* Quads R */}
      <path d="M230,274 C236,296 238,328 236,360" stroke={MUS} strokeWidth="9" fill="none" opacity="0.48" strokeLinecap="round"/>
      <line x1="233" y1="285" x2="235" y2="318" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      <line x1="227" y1="280" x2="229" y2="312" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      {/* Tibialis anterior L */}
      <path d="M155,412 C153,434 152,456 153,476" stroke={MUS} strokeWidth="5" fill="none" opacity="0.48" strokeLinecap="round"/>
      {/* Tibialis anterior R */}
      <path d="M235,412 C237,434 238,456 237,476" stroke={MUS} strokeWidth="5" fill="none" opacity="0.48" strokeLinecap="round"/>
      {/* Thenar eminence L (palm muscle) */}
      <path d="M104,280 C108,290 112,306 110,318" stroke={MUS} strokeWidth="3.5" fill="none" opacity="0.5" strokeLinecap="round"/>
      {/* Thenar eminence R */}
      <path d="M286,280 C282,290 278,306 280,318" stroke={MUS} strokeWidth="3.5" fill="none" opacity="0.5" strokeLinecap="round"/>

      {/* ── Organs (clipped) ── */}
      <g clipPath="url(#tcF)">
        <ellipse cx="172" cy="118" rx="18" ry="28" fill="rgba(220,140,150,0.45)"/>
        <ellipse cx="218" cy="118" rx="18" ry="28" fill="rgba(220,140,150,0.45)"/>
        <path d="M186,130 C184,124 178,122 176,128 C174,134 180,142 186,148 C192,142 198,134 196,128 C194,122 188,124 186,130Z" fill="rgba(200,40,60,0.7)"/>
        <path d="M195,170 C202,168 218,170 222,180 C224,190 218,196 210,196 C200,196 195,188 195,180Z" fill="rgba(148,60,28,0.55)"/>
        <ellipse cx="180" cy="182" rx="16" ry="12" fill="rgba(220,180,100,0.4)"/>
      </g>

      {/* ── Tendons ── */}
      <line x1="155" y1="270" x2="156" y2="386" stroke={TEND} strokeWidth="1.5" opacity="0.6"/>
      <line x1="235" y1="270" x2="234" y2="386" stroke={TEND} strokeWidth="1.5" opacity="0.6"/>
      <line x1="159" y1="399" x2="158" y2="508" stroke={TEND} strokeWidth="1.2" opacity="0.45"/>
      <line x1="231" y1="399" x2="232" y2="508" stroke={TEND} strokeWidth="1.2" opacity="0.45"/>
      {/* Achilles (front plantar fascia) */}
      <line x1="150" y1="504" x2="146" y2="558" stroke={TEND} strokeWidth="1.5" strokeLinecap="round" opacity="0.55"/>
      <line x1="240" y1="504" x2="244" y2="558" stroke={TEND} strokeWidth="1.5" strokeLinecap="round" opacity="0.55"/>
      {/* Plantar fascia */}
      <path d="M140,558 C140,570 140,582 140,594" stroke={TEND} strokeWidth="1" fill="none" opacity="0.45"/>
      <path d="M250,558 C250,570 250,582 250,594" stroke={TEND} strokeWidth="1" fill="none" opacity="0.45"/>
      {/* Flexor tendons L fingers */}
      <line x1="100" y1="278" x2="99" y2="350" stroke={TEND} strokeWidth="0.8" opacity="0.5"/>
      <line x1="93"  y1="278" x2="90" y2="350" stroke={TEND} strokeWidth="0.8" opacity="0.5"/>
      {/* Flexor tendons R fingers */}
      <line x1="290" y1="278" x2="291" y2="350" stroke={TEND} strokeWidth="0.8" opacity="0.5"/>
      <line x1="297" y1="278" x2="300" y2="350" stroke={TEND} strokeWidth="0.8" opacity="0.5"/>

      {/* ── Nerves ── */}
      <g filter="url(#ngF)">
        <path d="M188,76 C178,82 162,92 148,104" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M202,76 C212,82 228,92 242,104" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M136,168 C126,192 114,224 106,268" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
        <path d="M254,168 C264,192 276,224 284,268" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
        {/* Digital nerves L */}
        <path d="M88,270 C84,300 78,328 74,354" stroke={NERV} strokeWidth="0.8" fill="none" strokeDasharray="2,4" opacity="0.7"/>
        {/* Digital nerves R */}
        <path d="M302,270 C306,300 312,328 316,354" stroke={NERV} strokeWidth="0.8" fill="none" strokeDasharray="2,4" opacity="0.7"/>
        <path d="M175,252 C168,284 162,326 160,386" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M215,252 C222,284 228,326 230,386" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M160,402 C158,432 157,464 155,498" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
        <path d="M230,402 C232,432 233,464 235,498" stroke={NERV} strokeWidth="1" fill="none" strokeDasharray="3,4"/>
        {/* Plantar nerve L */}
        <path d="M152,508 C149,528 147,548 146,568" stroke={NERV} strokeWidth="0.9" fill="none" strokeDasharray="3,4" opacity="0.7"/>
        {/* Plantar nerve R */}
        <path d="M238,508 C241,528 243,548 244,568" stroke={NERV} strokeWidth="0.9" fill="none" strokeDasharray="3,4" opacity="0.7"/>
      </g>
    </g>
  );
}

function BackBodySVG() {
  const SKIN = "rgba(215,175,138,0.11)";
  const SSTR = "rgba(192,152,112,0.38)";
  const BONE = "rgba(238,222,182,0.90)";
  const BSTR = "rgba(205,188,140,0.88)";
  const MUS  = "rgba(186,52,32,0.65)";
  const MSTR = "rgba(145,28,16,0.58)";
  const TEND = "rgba(218,178,60,0.80)";
  const CART = "rgba(110,188,225,0.68)";
  const NERV = "rgba(252,214,28,0.80)";
  return (
    <g>
      <defs>
        <radialGradient id="bgGB" cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="#18263a"/>
          <stop offset="100%" stopColor="#0a1018"/>
        </radialGradient>
        <clipPath id="tcB"><rect x="152" y="155" width="86" height="62"/></clipPath>
        <filter id="ngB" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="1.8" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>
      <rect x="0" y="0" width="390" height="660" fill="url(#bgGB)"/>
      {/* ── Skin silhouettes ── */}
      <ellipse cx="195" cy="34" rx="26" ry="30" fill={SKIN} stroke={SSTR} strokeWidth="1.2"/>
      <rect x="185" y="62" width="20" height="24" rx="4" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M152,84 C145,100 140,162 142,200 C144,222 160,242 195,244 C230,242 246,222 248,200 C250,162 245,100 238,84 C228,78 210,74 195,74 C180,74 162,78 152,84Z" fill={SKIN} stroke={SSTR} strokeWidth="1"/>
      {/* Left arm back */}
      <path d="M152,90 C138,108 122,148 112,192 C108,210 110,240 120,240 C130,240 132,210 134,192 L148,144Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M108,240 C102,256 98,272 98,288 L108,292 L122,244Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right arm back */}
      <path d="M238,90 C252,108 268,148 278,192 C282,210 280,240 270,240 C260,240 258,210 256,192 L242,144Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      <path d="M282,240 C288,256 292,272 292,288 L282,292 L268,244Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Pelvis */}
      <path d="M155,202 C150,224 150,250 158,270 L192,272 L198,272 L232,270 C240,250 240,224 235,202Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left thigh back */}
      <path d="M158,272 C152,296 150,334 152,368 C154,382 163,388 174,384 L180,326 L192,272Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left lower leg back */}
      <path d="M152,372 C148,400 148,436 150,470 C152,484 162,490 174,486 L178,452 L178,376Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Left heel/foot back */}
      <path d="M148,474 C144,494 140,516 138,536 C136,556 138,572 144,580 C150,588 160,590 168,586 L172,572 C166,576 156,576 150,568 C144,558 144,542 146,526 C148,510 152,492 154,478Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right thigh back */}
      <path d="M232,272 C238,296 240,334 238,368 C236,382 227,388 216,384 L210,326 L198,272Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right lower leg back */}
      <path d="M238,372 C242,400 242,436 240,470 C238,484 228,490 216,486 L212,452 L212,376Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>
      {/* Right heel/foot back */}
      <path d="M242,474 C246,494 250,516 252,536 C254,556 252,572 246,580 C240,588 230,590 222,586 L218,572 C224,576 234,576 240,568 C246,558 246,542 244,526 C242,510 238,492 236,478Z" fill={SKIN} stroke={SSTR} strokeWidth="0.8"/>

      {/* ── Skeleton back ── */}
      {/* Skull back */}
      <ellipse cx="195" cy="30" rx="22" ry="25" fill="none" stroke={BONE} strokeWidth="1.2"/>
      {/* Thoracic spine 12 vertebrae */}
      {[90,101,112,123,134,144,154,163,172,180,188,196].map((y, i) => (
        <g key={i}>
          <rect x="191" y={y} width="8" height="9" rx="1" fill={BONE} stroke={BSTR} strokeWidth="0.5"/>
          <rect x="192" y={y+9} width="6" height="3" rx="0.5" fill={BSTR} strokeWidth="0"/>
          <line x1="188" y1={y+4} x2="180" y2={y+4} stroke={BSTR} strokeWidth="1"/>
          <line x1="202" y1={y+4} x2="210" y2={y+4} stroke={BSTR} strokeWidth="1"/>
        </g>
      ))}
      {/* Lumbar spine */}
      {[208,220,232].map((y, i) => (
        <g key={i}>
          <rect x="190" y={y} width="10" height="9" rx="1.5" fill={BONE} stroke={BSTR} strokeWidth="0.6"/>
          <line x1="186" y1={y+4} x2="176" y2={y+4} stroke={BSTR} strokeWidth="1.2"/>
          <line x1="204" y1={y+4} x2="214" y2={y+4} stroke={BSTR} strokeWidth="1.2"/>
        </g>
      ))}
      {/* Sacrum */}
      <path d="M185,244 L205,244 L208,268 L195,274 L182,268Z" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      {/* Coccyx */}
      <path d="M191,274 L199,274 L197,286 L195,288 L193,286Z" fill={BONE} stroke={BSTR} strokeWidth="0.7"/>
      {/* Scapulae L */}
      <path d="M150,92 C144,110 143,130 148,148 C156,156 168,154 172,144 C174,134 174,112 168,98 C164,92 156,88 150,92Z" fill="none" stroke={BONE} strokeWidth="1.5"/>
      <path d="M150,100 C158,104 166,112 170,122" stroke={BONE} strokeWidth="1.2" fill="none"/>
      <ellipse cx="150" cy="94" rx="6" ry="5" fill={BONE} opacity="0.5"/>
      {/* Scapulae R */}
      <path d="M240,92 C246,110 247,130 242,148 C234,156 222,154 218,144 C216,134 216,112 222,98 C226,92 234,88 240,92Z" fill="none" stroke={BONE} strokeWidth="1.5"/>
      <path d="M240,100 C232,104 224,112 220,122" stroke={BONE} strokeWidth="1.2" fill="none"/>
      <ellipse cx="240" cy="94" rx="6" ry="5" fill={BONE} opacity="0.5"/>
      {/* Pelvis back */}
      <path d="M155,204 C148,214 145,228 148,242 C155,252 170,258 195,260 C220,258 235,252 242,242 C245,228 242,214 235,204" fill="none" stroke={BONE} strokeWidth="1.5"/>
      <path d="M148,208 C143,204 140,196 145,190 C152,184 170,182 195,182 C220,182 238,184 245,190 C250,196 247,204 242,208" fill="none" stroke={BONE} strokeWidth="1.2"/>
      {/* Humerus L */}
      <line x1="150" y1="98" x2="108" y2="210" stroke={BONE} strokeWidth="2.4" strokeLinecap="round"/>
      {/* Ulna/Radius L back */}
      <line x1="104" y1="216" x2="92" y2="268" stroke={BONE} strokeWidth="2.0" strokeLinecap="round"/>
      <line x1="112" y1="216" x2="102" y2="266" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      {/* Humerus R */}
      <line x1="240" y1="98" x2="282" y2="210" stroke={BONE} strokeWidth="2.4" strokeLinecap="round"/>
      {/* Ulna/Radius R back */}
      <line x1="286" y1="216" x2="298" y2="268" stroke={BONE} strokeWidth="2.0" strokeLinecap="round"/>
      <line x1="278" y1="216" x2="288" y2="266" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      {/* Femurs back */}
      <line x1="168" y1="256" x2="162" y2="408" stroke={BONE} strokeWidth="2.8" strokeLinecap="round"/>
      <line x1="222" y1="256" x2="228" y2="408" stroke={BONE} strokeWidth="2.8" strokeLinecap="round"/>
      {/* Tibia/Fibula L back */}
      <line x1="160" y1="420" x2="158" y2="510" stroke={BONE} strokeWidth="2.4" strokeLinecap="round"/>
      <line x1="169" y1="422" x2="167" y2="506" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      {/* Tibia/Fibula R back */}
      <line x1="230" y1="420" x2="232" y2="510" stroke={BONE} strokeWidth="2.4" strokeLinecap="round"/>
      <line x1="221" y1="422" x2="223" y2="506" stroke={BONE} strokeWidth="1.3" strokeLinecap="round"/>
      {/* Calcaneus (heel bone) L */}
      <ellipse cx="151" cy="548" rx="14" ry="10" fill={BONE} stroke={BSTR} strokeWidth="0.9"/>
      {/* Calcaneus R */}
      <ellipse cx="239" cy="548" rx="14" ry="10" fill={BONE} stroke={BSTR} strokeWidth="0.9"/>

      {/* ── Joints back ── */}
      <circle cx="150" cy="94" r="9" fill={CART} opacity="0.45"/>
      <circle cx="240" cy="94" r="9" fill={CART} opacity="0.45"/>
      <ellipse cx="108" cy="213" rx="9" ry="7" fill={CART} opacity="0.45"/>
      <ellipse cx="282" cy="213" rx="9" ry="7" fill={CART} opacity="0.45"/>
      {/* Olecranon (elbow back) */}
      <ellipse cx="108" cy="215" rx="8" ry="6" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      <ellipse cx="282" cy="215" rx="8" ry="6" fill={BONE} stroke={BSTR} strokeWidth="0.8"/>
      {/* Knee popliteal L */}
      <ellipse cx="162" cy="412" rx="12" ry="8" fill={CART} opacity="0.4"/>
      {/* Knee popliteal R */}
      <ellipse cx="228" cy="412" rx="12" ry="8" fill={CART} opacity="0.4"/>
      {/* Ankle back L */}
      <ellipse cx="156" cy="512" rx="11" ry="7" fill={CART} opacity="0.4"/>
      {/* Ankle back R */}
      <ellipse cx="234" cy="512" rx="11" ry="7" fill={CART} opacity="0.4"/>
      {/* Achilles tendons */}
      <line x1="155" y1="516" x2="152" y2="542" stroke={TEND} strokeWidth="3.5" strokeLinecap="round"/>
      <line x1="235" y1="516" x2="238" y2="542" stroke={TEND} strokeWidth="3.5" strokeLinecap="round"/>

      {/* ── Muscles back ── */}
      {/* Trapezius upper */}
      <path d="M186,82 C180,88 162,96 155,104 C162,108 176,112 186,110 L195,106 L204,110 C214,112 228,108 235,104 C228,96 210,88 204,82Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      {/* Trapezius mid */}
      <path d="M155,104 C150,118 149,136 153,150 C161,155 178,154 186,148 L186,110 L195,106 L204,110 L204,148 C212,154 229,155 237,150 C241,136 240,118 235,104" fill={MUS} opacity="0.42" stroke={MSTR} strokeWidth="0.7"/>
      {/* Rhomboid fibers */}
      <line x1="186" y1="112" x2="164" y2="126" stroke={MSTR} strokeWidth="0.8" opacity="0.5"/>
      <line x1="186" y1="120" x2="160" y2="134" stroke={MSTR} strokeWidth="0.8" opacity="0.5"/>
      <line x1="204" y1="112" x2="226" y2="126" stroke={MSTR} strokeWidth="0.8" opacity="0.5"/>
      <line x1="204" y1="120" x2="230" y2="134" stroke={MSTR} strokeWidth="0.8" opacity="0.5"/>
      {/* Infraspinatus L */}
      <path d="M150,102 C144,114 144,130 148,144 C154,150 164,150 168,140 C170,130 168,114 164,104Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      {/* Infraspinatus R */}
      <path d="M240,102 C246,114 246,130 242,144 C236,150 226,150 222,140 C220,130 222,114 226,104Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      {/* Latissimus L */}
      <path d="M155,108 C148,130 144,162 148,194 C155,202 170,204 180,194 L178,148 C172,134 163,120 155,108Z" fill={MUS} opacity="0.5" stroke={MSTR} strokeWidth="0.8"/>
      {/* Latissimus R */}
      <path d="M235,108 C242,130 246,162 242,194 C235,202 220,204 210,194 L212,148 C218,134 227,120 235,108Z" fill={MUS} opacity="0.5" stroke={MSTR} strokeWidth="0.8"/>
      {/* Erector spinae */}
      <rect x="184" y="152" width="7" height="58" rx="3" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      <rect x="199" y="152" width="7" height="58" rx="3" fill={MUS} opacity="0.45" stroke={MSTR} strokeWidth="0.7"/>
      <line x1="186" y1="162" x2="186" y2="205" stroke={MSTR} strokeWidth="0.8" opacity="0.6"/>
      <line x1="204" y1="162" x2="204" y2="205" stroke={MSTR} strokeWidth="0.8" opacity="0.6"/>
      {/* Deltoids back L/R */}
      <path d="M148,88 C140,96 134,112 136,126 C140,130 148,126 152,116 L154,98Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      <path d="M242,88 C250,96 256,112 254,126 C250,130 242,126 238,116 L236,98Z" fill={MUS} opacity="0.6" stroke={MSTR} strokeWidth="0.8"/>
      {/* Triceps L */}
      <path d="M144,120 C136,140 132,164 134,180 C138,186 144,184 148,176 C146,158 146,140 148,122Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      {/* Triceps R */}
      <path d="M246,120 C254,140 258,164 256,180 C252,186 246,184 242,176 C244,158 244,140 242,122Z" fill={MUS} opacity="0.55" stroke={MSTR} strokeWidth="0.7"/>
      {/* Gluteus maximus L */}
      <path d="M158,204 C150,220 148,242 154,260 C160,268 174,272 184,264 L188,224Z" fill={MUS} opacity="0.58" stroke={MSTR} strokeWidth="0.8"/>
      {/* Gluteus maximus R */}
      <path d="M232,204 C240,220 242,242 236,260 C230,268 216,272 206,264 L202,224Z" fill={MUS} opacity="0.58" stroke={MSTR} strokeWidth="0.8"/>
      {/* Hamstrings L */}
      <path d="M160,274 C153,300 151,334 153,368" stroke={MUS} strokeWidth="9" fill="none" opacity="0.46" strokeLinecap="round"/>
      <line x1="157" y1="286" x2="155" y2="320" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      <line x1="164" y1="280" x2="162" y2="314" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      {/* Hamstrings R */}
      <path d="M230,274 C237,300 239,334 237,368" stroke={MUS} strokeWidth="9" fill="none" opacity="0.46" strokeLinecap="round"/>
      <line x1="233" y1="286" x2="235" y2="320" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      <line x1="226" y1="280" x2="228" y2="314" stroke={MSTR} strokeWidth="1" opacity="0.5"/>
      {/* Gastrocnemius L */}
      <path d="M156,378 C151,396 150,416 154,434 C158,442 165,442 168,434 C165,418 162,400 160,382Z" fill={MUS} opacity="0.58" stroke={MSTR} strokeWidth="0.8"/>
      {/* Soleus L */}
      <path d="M161,434 C159,448 158,462 158,476" stroke={MUS} strokeWidth="4" fill="none" opacity="0.42" strokeLinecap="round"/>
      {/* Gastrocnemius R */}
      <path d="M234,378 C239,396 240,416 236,434 C232,442 225,442 222,434 C225,418 228,400 230,382Z" fill={MUS} opacity="0.58" stroke={MSTR} strokeWidth="0.8"/>
      {/* Soleus R */}
      <path d="M229,434 C231,448 232,462 232,476" stroke={MUS} strokeWidth="4" fill="none" opacity="0.42" strokeLinecap="round"/>

      {/* ── Organs (kidneys) ── */}
      <g clipPath="url(#tcB)">
        <path d="M165,164 C159,164 155,170 155,178 C155,186 159,192 165,194 C170,194 174,190 175,184 C175,176 171,166 168,164Z" fill="rgba(180,60,40,0.5)"/>
        <line x1="165" y1="194" x2="165" y2="206" stroke="rgba(180,60,40,0.4)" strokeWidth="1.5"/>
        <path d="M225,164 C231,164 235,170 235,178 C235,186 231,192 225,194 C220,194 216,190 215,184 C215,176 219,166 222,164Z" fill="rgba(180,60,40,0.5)"/>
        <line x1="225" y1="194" x2="225" y2="206" stroke="rgba(180,60,40,0.4)" strokeWidth="1.5"/>
        <ellipse cx="166" cy="162" rx="6" ry="4" fill="rgba(220,160,60,0.55)"/>
        <ellipse cx="224" cy="162" rx="6" ry="4" fill="rgba(220,160,60,0.55)"/>
      </g>

      {/* ── IT bands ── */}
      <line x1="154" y1="270" x2="155" y2="408" stroke={TEND} strokeWidth="1.5" opacity="0.55"/>
      <line x1="236" y1="270" x2="235" y2="408" stroke={TEND} strokeWidth="1.5" opacity="0.55"/>

      {/* ── Nerves back ── */}
      <g filter="url(#ngB)">
        <path d="M195,62 C195,100 195,140 195,202" stroke={NERV} strokeWidth="1.5" fill="none" strokeDasharray="5,4"/>
        <path d="M188,76 C178,82 162,92 148,104" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        <path d="M202,76 C212,82 228,92 242,104" stroke={NERV} strokeWidth="1.2" fill="none" strokeDasharray="4,3"/>
        {/* Sciatic L */}
        <path d="M174,258 C167,282 162,326 160,368" stroke={NERV} strokeWidth="1.6" fill="none" strokeDasharray="5,3"/>
        {/* Sciatic R */}
        <path d="M216,258 C223,282 228,326 230,368" stroke={NERV} strokeWidth="1.6" fill="none" strokeDasharray="5,3"/>
        {/* Tibial nerve L */}
        <path d="M160,380 C159,410 158,444 157,476" stroke={NERV} strokeWidth="1.1" fill="none" strokeDasharray="3,4"/>
        {/* Tibial nerve R */}
        <path d="M230,380 C231,410 232,444 233,476" stroke={NERV} strokeWidth="1.1" fill="none" strokeDasharray="3,4"/>
        {/* Sural nerve to heel L */}
        <path d="M158,476 C155,498 152,522 150,544" stroke={NERV} strokeWidth="0.9" fill="none" strokeDasharray="3,4" opacity="0.7"/>
        {/* Sural nerve to heel R */}
        <path d="M232,476 C235,498 238,522 240,544" stroke={NERV} strokeWidth="0.9" fill="none" strokeDasharray="3,4" opacity="0.7"/>
      </g>
    </g>
  );
}
